import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import {
  BarChart3,
  BookOpenText,
  ChevronRight,
  CloudSun,
  Camera,
  Check,
  Clock3,
  Download,
  Droplets,
  FileText,
  FlaskConical,
  GitCompare,
  Leaf,
  Link2,
  LoaderCircle,
  LogOut,
  MapPinned,
  Menu,
  ImagePlus,
  PackageOpen,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Scale,
  Save,
  ShieldCheck,
  ShoppingCart,
  Square,
  UserRound,
  X
} from "lucide-react";
import { api, ApiError, bootstrap, Permission, User } from "./lib/api";

type Page = "dashboard" | "new" | "journal" | "recipes" | "nutrition" | "stock" | "zones" | "water" | "share" | "access";

function can(user: User, permission: Permission) {
  return user.permissions.includes(permission);
}

type DashboardData = {
  kpi: {
    application_count: number;
    refill_total_liters: string;
    total_cost: string;
    latest_hydrawise_at: string | null;
    hydrawise_state: "waiting" | "connected" | "partial" | "error";
    hydrawise_label: string;
    hydrawise_detail: string;
  };
  stock: Array<{ id: string; legacy_id: string; name: string; status: string; balance_grams: string }>;
  forecast: {
    calculated_at: string;
    as_of_date: string;
    confidence: "low" | "medium" | "high";
    confidence_label: string;
    stale_hydrawise: boolean;
    hydrawise_cutoff_at: string | null;
    summary: { materials_to_order: number; materials_planned: number; next_order_window_from: string | null; next_order_window_to: string | null; red_count: number; yellow_count: number; horizon_end: string; operational_alert: "red" | null };
    season: {
      state: "not_started" | "active" | "paused" | "tank_likely_empty" | "closed";
      label: string;
      start: string | null;
      fertilization_started_at: string | null;
      irrigation_start_observed_at: string | null;
      expected_end: string | null;
      last_application_date: string | null;
      last_eligible_irrigation_date: string | null;
      estimated_tank_remaining_liters: string;
      next_refill_earliest: string | null;
      next_refill_latest: string | null;
    };
    next_season: { start: string; start_earliest: string; start_latest: string; expected_end: string; recipe_kind: string; label: string };
    rate: {
      likely_liters_per_calendar_day: string;
      slow_liters_per_calendar_day: string;
      fast_liters_per_calendar_day: string;
      complete_intervals: number;
      hydrawise_covered_intervals: number;
    };
    materials: Array<{
      id: string;
      legacy_id: string;
      name: string;
      balance_grams: string;
      current_dose_grams_per_100_liters: string;
      current_season_need_grams: string;
      next_season_need_grams: string;
      balance_after_current_season_grams: string;
      safety_grams: string;
      total_horizon_need_grams: string;
      deficit_grams: string;
      package_grams: string | null;
      packs_to_buy: number;
      planned_packs: number;
      buy_now: boolean;
      purchase_state: "covered" | "scheduled" | "purchase_window" | "plan_overdue" | "urgent" | "shortage" | "unknown";
      order_window_from: string | null;
      order_window_to: string | null;
      purchase_quantity_grams: string;
      carryover_after_next_season_grams: string;
      alert_level: "red" | "yellow" | "green" | "neutral" | "unknown";
      alert_label: string;
      shortage_earliest: string | null;
      shortage_likely: string | null;
      shortage_latest: string | null;
      active_watering_days_remaining: number | null;
    }>;
  };
};
type NutritionData = {
  calculated_at: string;
  as_of_date: string;
  quality: "measured" | "estimated" | "limited";
  quality_label: string;
  program: {
    name: string;
    version: string;
    status: string;
    source: string;
    source_note: string;
    area_m2: string;
    actual_tracking_from: string;
    boundary_policy: string;
  };
  months: Array<{
    month: number;
    reference_only: boolean;
    quality: string;
    nutrients: Array<{
      code: string;
      name: string;
      basis: string;
      display_unit: "g_m2" | "mg_m2";
      target: string;
      expected: string;
      delivered: string;
      projected: string;
      completion_percent: string;
      recipe_fit_percent: string | null;
      quality: string;
    }>;
  }>;
  rate: {
    calendar_liters_per_day: string | null;
    runtime_liters_per_minute: string | null;
    closed_intervals: number;
  };
  tank: { capacity_liters: string; latest_fill_date: string | null; open_interval_quality: string };
  intervals: Array<{ from: string; to: string; consumed_liters: string; runtime_minutes: string; quality: string; mass_conservation: string }>;
  applications: Array<{ id: string; date: string; remainder_liters: string; work_volume_liters: string; materials: Array<{ name: string; grams: string }> }>;
  compositions: Array<{ material: string; product_label: string; manufacturer: string | null; version: number; status: string; effective_from: string; source: string; snapshot_sha256: string; contents_percent: Record<string, string> }>;
  source_cutoff: { application_recorded_at: string | null; hydrawise_event_at: string | null; hydrawise_sync_at: string | null };
  assumptions: string[];
};
type Application = {
  id: string;
  operation_date: string;
  remainder_liters: string;
  work_volume_liters: string;
  refill_liters: string;
  total_cost: string | null;
  comment: string | null;
  status: string;
  lines: number;
  recorded_at: string;
  author_label: string;
  zones: Array<{ zone_number: number; name: string }>;
};
type MaterialAdjustment = {
  legacy_id: string;
  name: string;
  primary_nutrient: string | null;
  standard_grams: string;
  adaptive_grams: string;
  delta_grams: string;
  delta_percent: string | null;
  direction: "increased" | "decreased" | "unchanged";
  reason_codes: string[];
  explanation: string;
  evidence_type: string;
};
type CalculationSnapshot = {
  mode: string;
  reason?: string;
  phase?: "growth" | "active_growth" | "heat_stress" | "autumn" | "finishing";
  phase_label?: string;
  horizon_end?: string;
  pace_liters_per_day?: string;
  growth_observation?: "weak" | "normal" | "excess_or_damage" | "no_growth";
  solution_state?: "clear" | "sediment";
  crosses_phase_boundary?: boolean;
  warnings?: string[];
  residual_mg?: Record<string, string>;
  measurement_explanation?: string;
  material_adjustments?: MaterialAdjustment[];
  safety_policy?: {
    version?: string;
    recoverable_debt_days?: number;
    max_recovery_fraction_of_future_target?: string;
    max_n_recovery_mg_m2?: string;
    single_tank_phosphate_magnesium_status?: "visual_clarity_required" | "not_applicable";
  };
  irrigation_support?: {
    state?: "confirmed_all_zones" | "not_confirmed";
    catchup_allowed?: boolean;
    lookback_days?: number;
    meaning?: string;
  };
  hydrawise_evidence?: {
    rainfall_mm?: string;
    actual_irrigation_days?: number;
    weather_reduced_days?: string[];
    rain_without_irrigation_days?: string[];
    report_freshness?: { state?: string; finished_at?: string | null };
    water_balance_assessment?: {
      state?: "not_inferred";
      label?: string;
      explanation?: string;
    };
  };
};
type ApplicationDetail = {
  id: string;
  legacy_id: string | null;
  operation_date: string;
  recorded_at: string;
  created_at: string;
  remainder_liters: string;
  work_volume_liters: string;
  refill_liters: string;
  total_cost: string | null;
  comment: string | null;
  status: string;
  source: string;
  source_label: string;
  author: { label: string; source: string; source_label: string; confidence: string };
  recipe: { id: string; label: string; snapshot_sha256: string | null } | null;
  zones: Array<{ id: string; zone_number: number; name: string }>;
  lines: Array<{
    nomenclature_id: string;
    legacy_id: string;
    name: string;
    grams: string;
    price_per_gram: string | null;
    cost: string | null;
  }>;
  reversal: { reversed_at: string; comment: string; author_label: string | null } | null;
  calculation: CalculationSnapshot | null;
};
type Zone = {
  id: string;
  zone_number: number;
  name: string;
  irrigation_type: "drip" | "lawn";
  participates_in_fertilization: boolean;
  photo_path: string;
  photo_srcset: { "400": string; "800": string; "1600": string };
  active_photo_version_id: string | null;
  has_photo_history: boolean;
  hydrawise_mapping_state: "mapped" | "awaiting_api_confirmation";
  application_count: number;
};
type ZonePhotoVersion = {
  id: string;
  status: "draft" | "processing" | "active" | "superseded" | "failed";
  source_kind: "upload" | "bundled";
  width: number | null;
  height: number | null;
  crop: Record<string, number>;
  created_at: string;
  activated_at: string | null;
  author_label?: string;
  urls: Record<string, string>;
};
type IrrigationCommand = {
  id: string;
  idempotency_key: string;
  zone_id: string;
  zone_number: number;
  zone_name: string;
  state: "requested" | "sending" | "reconciling" | "running" | "stopping" | "completed" | "stopped" | "rejected" | "failed_safety_stop";
  duration_seconds: number;
  ignore_rain_sensor: boolean;
  queue_on_conflict: boolean;
  requested_at: string;
  confirmed_started_at: string | null;
  expected_end_at: string | null;
  completed_at: string | null;
  error_code: string | null;
  error_message: string | null;
};
type IrrigationControlStatus = {
  available: boolean;
  provider: string;
  message: string;
  supports: { start: boolean; zone_stop: boolean; live_status: boolean };
  controller: {
    online: boolean;
    active_zone_external_id: string | null;
    active_zone_number?: number | null;
    active_zone_name?: string | null;
    remaining_seconds: number | null;
    rain_sensor_active: boolean | null;
    schedule_conflict: boolean;
    observed_at: string;
  } | null;
  active_commands: IrrigationCommand[];
  defaults: {
    ignore_rain_sensor: boolean;
    queue_on_conflict: boolean;
    duration_seconds: number;
    max_duration_seconds: number;
  };
};
type Preview = {
  operation_date: string;
  remainder_liters: string;
  work_volume_liters: string;
  refill_liters: string;
  recipe_name: string;
  total_cost: string | null;
  zones: Array<{ id: string; zone_number: number; name: string }>;
  lines: Array<{ legacy_id: string; name: string; grams: string; cost: string | null }>;
  calculation_mode: "adaptive" | "adaptive_degraded" | "published_recipe_fallback";
  calculation_sha256: string;
  calculation_snapshot: CalculationSnapshot;
  comment?: string | null;
  refill_id: string | null;
  refill_status: "calculated" | "started" | null;
};
type NutritionSeason = {
  id: string;
  year: number;
  state: "closed" | "commissioning" | "active_growth" | "heat_stress" | "autumn" | "finishing";
  label: string;
  data_quality: "complete" | "estimated" | "incomplete";
  active_from: string | null;
  autumn_from: string | null;
  closed_at: string | null;
  closing_remainder_liters: string | null;
  next_action: string;
  can_start: boolean;
  can_finish: boolean;
  can_close: boolean;
  controller_schedule_control: "external_hydrawise_required";
  policy: {
    source: string;
    growth_months: number[];
    autumn_months: number[];
    growth_debt_resets_on: string;
    year_debt_resets_on_close: boolean;
    boundary_evidence: {
      material?: string;
      august_grams_per_100_liters?: string;
      september_grams_per_100_liters?: string;
    };
  };
};
type AdaptivePolicyStatus = {
  version: string;
  status: string;
  effective_from: string;
  snapshot_sha256: string;
  levels: Record<string, Record<string, string>>;
  readiness: {
    calculator: string;
    refill_ledger: string;
    hydrawise_evidence: string;
    fertika_credit: string;
    magnesium_material: string;
    growth_3: string;
  };
  latest_refill: { id: string; status: string; operation_date: string; growth_observation: string; solution_state: string } | null;
  fertika_credit: { id: string; applied_on: string; credit_to: string; dose_grams_m2: number } | null;
  growth_3_qualification: { id: string; result: "passed" | "failed"; started_at: string; finished_at: string; solution_clear: boolean; injector_filter_checked: boolean; snapshot_sha256: string } | null;
  calculation_deadline: { state: "not_scheduled" | "scheduled" | "preliminary" | "required" | "overdue"; next_refill_latest: string | null; hours_remaining: string | null; message: string };
};
type RecipeLine = {
  id: string;
  month: number;
  month_name: string;
  nomenclature_id: string;
  legacy_id: string;
  name: string;
  grams_per_100_liters: string;
};
type RecipeVersion = {
  id: string;
  name: string;
  version: number;
  label: string;
  status: "draft" | "published" | "active" | "rejected";
  display_status: "draft" | "scheduled" | "current" | "superseded" | "rejected";
  effective_from: string;
  effective_to: string | null;
  source: string;
  source_payload: Record<string, unknown>;
  change_reason: string | null;
  parent_version_id: string | null;
  parent_label: string | null;
  created_at: string;
  created_by_name?: string | null;
  published_at: string | null;
  published_by_name?: string | null;
  rejected_at: string | null;
  rejected_by_name?: string | null;
  rejection_reason: string | null;
  snapshot_sha256: string | null;
  document_sha256: string | null;
  has_document: boolean;
  zones: Array<{ id: string; zone_number: number; name: string }>;
  lines: RecipeLine[];
};
type RecipeCatalog = {
  on_date: string;
  current: RecipeVersion | null;
  draft: RecipeVersion | null;
  history: RecipeVersion[];
  eligible_zones: Array<{ id: string; zone_number: number; name: string }>;
  materials: Array<{ id: string; legacy_id: string; name: string }>;
};
type RecipeDiff = { month: number; month_name: string; nomenclature_id: string; name: string; before: string | null; after: string | null };
type StockItem = {
  id: string;
  legacy_id: string;
  name: string;
  status: string;
  balance_grams: string;
  unit_cost: string | null;
  balance_cost: string | null;
};
type StockDocument = {
  id: string;
  document_type: "purchase" | "inventory" | "reversal";
  status: "committed" | "reversed";
  operation_date: string;
  comment: string | null;
  source: string;
  metadata: Record<string, string>;
  reversal_of_id: string | null;
  total_cost: number | string;
  lines: Array<{
    nomenclature_id: string;
    name: string;
    quantity_grams: string;
    expected_balance_grams: string | null;
    actual_balance_grams: string | null;
  }>;
};
type AnalyticsData = {
  period: { from: string; to: string; group_by: "day" | "week" | "month" };
  zone_number: number | null;
  weather_scope: "controller";
  summary: {
    irrigation_minutes: number;
    rainfall_mm: number;
    evapotranspiration_mm: number;
    application_count: number;
    refill_liters: number;
  };
  series: Array<{
    period_start: string;
    irrigation_minutes: number;
    rainfall_mm: number;
    evapotranspiration_mm: number;
    application_count: number;
    refill_liters: number;
  }>;
  limitations: string[];
};

const navigation: Array<{ id: Page; label: string; icon: typeof BarChart3 }> = [
  { id: "dashboard", label: "Обзор", icon: BarChart3 },
  { id: "new", label: "Внесение", icon: Plus },
  { id: "journal", label: "Журнал", icon: BookOpenText },
  { id: "recipes", label: "Рецептуры", icon: FlaskConical },
  { id: "nutrition", label: "Баланс питания", icon: Leaf },
  { id: "stock", label: "Склад", icon: PackageOpen },
  { id: "zones", label: "Зоны", icon: MapPinned },
  { id: "water", label: "Полив", icon: Droplets },
  { id: "share", label: "Анализ", icon: Link2 },
  { id: "access", label: "Доступ", icon: UserRound }
];

function moscowToday(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Moscow",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function formatNumber(value: string | number, digits = 0): string {
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: digits }).format(Number(value));
}

function formatMass(value: string | number): string {
  const grams = Number(value);
  return grams >= 1000 ? `${formatNumber(grams / 1000, 1)} кг` : `${formatNumber(grams, 0)} г`;
}

function materialCountLabel(value: number): string {
  const mod100 = value % 100;
  const mod10 = value % 10;
  if (mod100 >= 11 && mod100 <= 14) return `${value} материалов`;
  if (mod10 === 1) return `${value} материал`;
  if (mod10 >= 2 && mod10 <= 4) return `${value} материала`;
  return `${value} материалов`;
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", { timeZone: "Europe/Moscow", day: "numeric", month: "long" }).format(
    new Date(`${value}T12:00:00+03:00`)
  );
}

function formatForecastDate(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", { timeZone: "Europe/Moscow", day: "numeric", month: "long", year: "numeric" }).format(
    new Date(`${value}T12:00:00+03:00`)
  );
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", {
    timeZone: "Europe/Moscow", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
  }).format(new Date(value));
}

function Login({ onLogin }: { onLogin: (user: User) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<"idle" | "loading">("idle");
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setState("loading");
    setError("");
    try {
      const user = await api<User>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password })
      });
      onLogin(user);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Не удалось войти");
    } finally {
      setState("idle");
    }
  }

  return (
    <main className="login-page">
      <section className="login-card">
        <div className="brand-mark large"><Leaf aria-hidden="true" /></div>
        <p className="eyebrow">Система ухода</p>
        <h1>Газон</h1>
        <p className="login-lead">Полив, фертигация и состояние участка в одном спокойном интерфейсе.</p>
        <form onSubmit={submit} className="login-form">
          <label>
            <span>Email</span>
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="username" required />
          </label>
          <label>
            <span>Пароль</span>
            <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" required />
          </label>
          {error && <div className="notice error" role="alert">{error}</div>}
          <button className="primary-button" disabled={state === "loading"}>
            {state === "loading" ? <><LoaderCircle className="spin" />Проверяю…</> : "Войти"}
          </button>
        </form>
        <p className="security-note"><ShieldCheck /> Защищённый вход Gazon</p>
      </section>
    </main>
  );
}

function Activation({ token, onActivated }: { token: string; onActivated: (user: User) => void }) {
  const [account, setAccount] = useState<{ email: string; name: string } | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api<{ email: string; name: string }>(`/api/auth/activation/${token}`)
      .then(setAccount)
      .catch((reason) => setError(reason instanceof ApiError ? reason.message : "Ссылка активации недоступна"))
      .finally(() => setLoading(false));
  }, [token]);

  async function activate(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) { setError("Пароли не совпадают"); return; }
    setLoading(true); setError("");
    try {
      const user = await api<User>(`/api/auth/activation/${token}`, {
        method: "POST",
        body: JSON.stringify({ password })
      });
      window.history.replaceState({}, "", "/");
      onActivated(user);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Не удалось активировать учетную запись");
    } finally { setLoading(false); }
  }

  return <main className="login-page"><section className="login-card"><div className="brand-mark large"><Leaf /></div><p className="eyebrow">Первый вход</p><h1>Газон</h1>{loading && !account ? <Loading label="Проверяю ссылку…" /> : account ? <><p className="login-lead">{account.name}, задайте пароль для учетной записи <strong>{account.email}</strong>.</p><form onSubmit={activate} className="login-form"><label><span>Новый пароль</span><input type="password" minLength={12} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label><label><span>Повторите пароль</span><input type="password" minLength={12} autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label><div className="notice neutral">Не менее 12 символов, буквы и цифры. Пароль нигде не отправляется и хранится только в защищенном виде.</div>{error && <div className="notice error" role="alert">{error}</div>}<button className="primary-button" disabled={loading}>{loading ? <><LoaderCircle className="spin" />Активирую…</> : "Активировать и войти"}</button></form></> : <div className="notice error" role="alert">{error}</div>}</section></main>;
}

function Dashboard({ data, loading, reload }: { data: DashboardData | null; loading: boolean; reload: () => void }) {
  if (loading && !data) return <Loading label="Собираю показатели…" />;
  if (!data) return <EmptyState title="Не удалось загрузить обзор" action={reload} />;
  const activeStock = data.stock.filter((item) => item.status === "active");
  const max = Math.max(...activeStock.map((item) => Number(item.balance_grams)), 1);
  return (
    <div className="page-stack">
      <section className="page-heading">
        <div><p className="eyebrow">Сегодня · Москва</p><h2>Состояние газона</h2></div>
        <button className="icon-button" onClick={reload} aria-label="Обновить"><RefreshCw className={loading ? "spin" : ""} /></button>
      </section>
      <section className="kpi-grid">
        <article className="kpi-card accent"><span>Внесений</span><strong>{data.kpi.application_count}</strong><small>подтверждённых</small></article>
        <article className="kpi-card"><span>Долив</span><strong>{formatNumber(data.kpi.refill_total_liters)} <em>л</em></strong><small>за весь период</small></article>
        <article className="kpi-card"><span>Стоимость</span><strong>{formatNumber(data.kpi.total_cost, 2)} <em>₽</em></strong><small>исторический факт</small></article>
        <article className="kpi-card"><span>Hydrawise</span><strong className={`status-text ${data.kpi.hydrawise_state}`}>{data.kpi.hydrawise_label}</strong><small>{data.kpi.latest_hydrawise_at ? `обновлён ${formatDateTime(data.kpi.latest_hydrawise_at)}` : data.kpi.hydrawise_detail}</small></article>
      </section>
      <section className="forecast-overview">
        <article className="forecast-lead">
          <div>
            <span className="eyebrow">Автопрогноз материалов</span>
            <strong>{data.forecast.summary.materials_to_order}</strong>
            <p>{data.forecast.summary.materials_to_order > 0 ? data.forecast.summary.materials_to_order === 1 ? "материал нужно купить сейчас" : "материалов нужно купить сейчас" : "сейчас покупать не требуется"}</p>
          </div>
          <ShoppingCart />
          <small>{data.forecast.summary.materials_planned > 0 ? `В плане ${materialCountLabel(data.forecast.summary.materials_planned)}${data.forecast.summary.next_order_window_from ? ` · окно ${formatForecastDate(data.forecast.summary.next_order_window_from)}${data.forecast.summary.next_order_window_to ? `–${formatForecastDate(data.forecast.summary.next_order_window_to)}` : ""}` : ""}` : `Запаса хватает до ${formatForecastDate(data.forecast.summary.horizon_end)}`}</small>
        </article>
        <article className={`forecast-season ${data.forecast.season.state}`}>
          <div><Droplets /><span>{data.forecast.season.label}</span></div>
          <strong>{formatNumber(data.forecast.season.estimated_tank_remaining_liters, 1)} л</strong>
          <p>расчётный остаток раствора в бочке</p>
          <small>{data.forecast.season.next_refill_earliest ? `Следующая заправка ориентировочно ${formatDate(data.forecast.season.next_refill_earliest)}${data.forecast.season.next_refill_latest && data.forecast.season.next_refill_latest !== data.forecast.season.next_refill_earliest ? `–${formatDate(data.forecast.season.next_refill_latest)}` : ""}` : data.forecast.season.state === "paused" ? "Прогноз продолжится после возобновления полива" : "Дата следующей заправки пока не определяется"}</small>
        </article>
        <article className="forecast-quality">
          <div><Clock3 /><span>{data.forecast.confidence_label}</span></div>
          <strong>{formatNumber(data.forecast.rate.likely_liters_per_calendar_day, 1)} л/день</strong>
          <p>наблюдаемый средний темп</p>
          <small>{data.forecast.rate.complete_intervals} полных цикла · {data.forecast.rate.hydrawise_covered_intervals} с полной историей Hydrawise</small>
        </article>
      </section>
      {data.forecast.summary.operational_alert === "red" && <div className="notice operational-alert" role="alert"><Droplets /><span><strong>Бочка, вероятно, пуста.</strong> Проверьте остаток и заправьте её. Если заправка уже была, внесите её фактической датой — весь прогноз пересчитается автоматически.</span></div>}
      {data.forecast.stale_hydrawise && <div className="notice warning" role="status">Hydrawise давно не обновлялся. Прогноз сохранён по данным на {data.forecast.hydrawise_cutoff_at ? formatDateTime(data.forecast.hydrawise_cutoff_at) : "последнюю успешную синхронизацию"}.</div>}
      <section className="panel forecast-panel">
        <div className="panel-title"><div><p className="eyebrow">Закупка без ручных настроек</p><h3>На сколько хватит материалов</h3></div><ShoppingCart /></div>
        <div className="forecast-legend"><span className="red">срочно · до 30 дней</span><span className="yellow">окно закупки открыто</span><span className="green">покупка запланирована позже</span></div>
        <div className="forecast-materials">
          {data.forecast.materials.map((item) => (
            <details className={`forecast-material ${item.alert_level}`} key={item.id}>
              <summary>
                <span className="forecast-signal" aria-hidden="true" />
                <span className="forecast-material-name"><strong>{item.name}</strong><small>{item.alert_label}</small></span>
                <span className="forecast-balance"><small>На складе</small><strong>{formatMass(item.balance_grams)}</strong></span>
                <span className="forecast-runway"><small>Ожидаемый дефицит</small><strong>{item.shortage_earliest ? `${formatForecastDate(item.shortage_earliest)}${item.shortage_latest && item.shortage_latest !== item.shortage_earliest ? `–${formatForecastDate(item.shortage_latest)}` : ""}` : "не ожидается"}</strong></span>
                <span className="forecast-order"><small>Рекомендация</small><strong>{item.purchase_state === "unknown" ? "уточнить фасовку" : item.buy_now && item.planned_packs > 0 && item.package_grams ? `${item.planned_packs} × ${formatMass(item.package_grams)}` : item.planned_packs > 0 ? "не покупать сейчас" : "не покупать"}</strong></span>
                <ChevronRight />
              </summary>
              <div className="forecast-detail-grid">
                <span><small>До конца сезона · консервативно</small><strong>{formatMass(item.current_season_need_grams)}</strong></span>
                <span><small>Переходящий остаток</small><strong>{formatMass(item.balance_after_current_season_grams)}</strong></span>
                <span><small>Следующий сезон · консервативно</small><strong>{formatMass(item.next_season_need_grams)}</strong></span>
                <span><small>Плановая закупка</small><strong>{item.purchase_state === "unknown" ? "нужно уточнить фасовку" : item.planned_packs > 0 && item.package_grams ? `${item.planned_packs} × ${formatMass(item.package_grams)}` : "не требуется"}</strong></span>
                <span><small>Окно закупки</small><strong>{item.order_window_from ? `${formatForecastDate(item.order_window_from)}${item.order_window_to ? `–${formatForecastDate(item.order_window_to)}` : ""}` : "—"}</strong></span>
                <span><small>Страховой запас</small><strong>{formatMass(item.safety_grams)}</strong></span>
                <span><small>Остаток после следующего сезона</small><strong>{formatMass(item.carryover_after_next_season_grams)}</strong></span>
                <span><small>Текущая дозировка</small><strong>{formatNumber(item.current_dose_grams_per_100_liters, 1)} г/100 л</strong></span>
                <span><small>Расчётных дней расхода до дефицита</small><strong>{item.active_watering_days_remaining ?? "—"}</strong></span>
              </div>
            </details>
          ))}
        </div>
        <footer className="forecast-footnote">Следующий сезон ожидается {formatForecastDate(data.forecast.next_season.start_earliest)}–{formatForecastDate(data.forecast.next_season.start_latest)}; расчётная дата {formatForecastDate(data.forecast.next_season.start)}. {data.forecast.next_season.label}. Прогноз сам пересчитывается после поливов, заправок, складских операций и публикации рецептуры.</footer>
      </section>
      <section className="panel">
        <div className="panel-title"><div><p className="eyebrow">Склад</p><h3>Остатки материалов</h3></div><PackageOpen /></div>
        <div className="stock-list">
          {activeStock.map((item) => (
            <div className="stock-row" key={item.id}>
              <div><strong>{item.name}</strong><small>{formatNumber(item.balance_grams)} г</small></div>
              <div className="bar"><i style={{ width: `${Math.max(3, Number(item.balance_grams) / max * 100)}%` }} /></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function MaterialAdjustmentList({ calculation }: { calculation: CalculationSnapshot }) {
  const items = calculation.material_adjustments ?? [];
  if (!items.length) return null;
  const evidence = calculation.hydrawise_evidence;
  return (
    <section className="material-adjustment-block" aria-label="Почему изменён состав">
      <div className="material-adjustment-heading">
        <div><p className="eyebrow">Объяснение по каждому материалу</p><h4>Обычная норма и адаптивная доза</h4></div>
        <span className="status-pill">Сохранится в журнале</span>
      </div>
      {calculation.measurement_explanation && <div className="measurement-proof"><Droplets /><p>{calculation.measurement_explanation}</p></div>}
      {evidence && <div className="hydrawise-proof">
        <span><small>Осадки после прошлой заправки</small><strong>{formatNumber(evidence.rainfall_mm ?? "0", 1)} мм</strong></span>
        <span><small>Дней с фактическим поливом</small><strong>{evidence.actual_irrigation_days ?? 0}</strong></span>
        <span><small>Дождь без зафиксированного полива</small><strong>{evidence.rain_without_irrigation_days?.length ?? 0} дн.</strong></span>
        <span><small>Hydrawise сокращал полив</small><strong>{evidence.weather_reduced_days?.length ?? 0} дн.</strong></span>
      </div>}
      {evidence?.water_balance_assessment?.explanation && <div className="measurement-proof"><Droplets /><p>{evidence.water_balance_assessment.explanation}</p></div>}
      <div className="material-adjustments">
        {items.map((item) => {
          const delta = Number(item.delta_grams);
          const deltaText = `${delta > 0 ? "+" : ""}${formatNumber(item.delta_grams, 1)} г`;
          const percent = item.delta_percent === null ? "" : ` · ${Number(item.delta_percent) > 0 ? "+" : ""}${formatNumber(item.delta_percent, 0)}%`;
          return <article className={`material-adjustment ${item.direction}`} key={item.legacy_id}>
            <header><div><strong>{item.name}</strong><small>{item.legacy_id}</small></div><span>{item.direction === "increased" ? "Увеличено" : item.direction === "decreased" ? "Снижено" : "Без изменения"}</span></header>
            <div className="material-adjustment-values">
              <span><small>Обычно по рецептуре</small><strong>{formatNumber(item.standard_grams, 1)} г</strong></span>
              <ChevronRight />
              <span><small>Добавить сейчас</small><strong>{formatNumber(item.adaptive_grams, 1)} г</strong></span>
              <span className="material-delta"><small>Изменение</small><strong>{deltaText}{percent}</strong></span>
            </div>
            <p>{item.explanation}</p>
          </article>;
        })}
      </div>
    </section>
  );
}

function NewApplication({ onSaved, onOpenRecipes }: { onSaved: () => void; onOpenRecipes: () => void }) {
  const previewRef = useRef<HTMLElement | null>(null);
  const [date, setDate] = useState(moscowToday());
  const [remainder, setRemainder] = useState("");
  const [comment, setComment] = useState("");
  const [growthObservation, setGrowthObservation] = useState("normal");
  const [solutionState, setSolutionState] = useState("clear");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [state, setState] = useState<"idle" | "preview" | "saving">("idle");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(crypto.randomUUID());
  const [activeRecipe, setActiveRecipe] = useState<RecipeVersion | null>(null);
  const usesAdaptive2027 = date >= "2027-01-01";

  useEffect(() => {
    api<RecipeCatalog>(`/api/recipes?on_date=${date}`).then((result) => setActiveRecipe(result.current)).catch(() => setActiveRecipe(null));
  }, [date]);

  useEffect(() => {
    api<Preview | null>("/api/applications/refills/active")
      .then((result) => {
        if (!result) return;
        setDate(result.operation_date);
        setRemainder(result.remainder_liters);
        setComment(result.comment ?? "");
        setGrowthObservation(result.calculation_snapshot.growth_observation ?? "normal");
        setSolutionState(result.calculation_snapshot.solution_state ?? "clear");
        setPreview(result);
        setSuccess("Начатая заправка восстановлена. Не добавляйте материалы повторно — завершите сохранение этой операции.");
      })
      .catch((reason) => setError(reason instanceof ApiError ? reason.message : "Не удалось проверить незавершённую заправку"));
  }, []);

  useEffect(() => {
    if (!preview) return;
    const frame = window.requestAnimationFrame(() => {
      previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      previewRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [preview]);

  async function calculate(event: FormEvent) {
    event.preventDefault();
    setState("preview"); setError(""); setSuccess("");
    try {
      const result = await api<Preview>("/api/applications/preview", {
        method: "POST",
        body: JSON.stringify({
          operation_date: date,
          remainder_liters: remainder,
          work_volume_liters: 90,
          comment,
          growth_observation: usesAdaptive2027 ? growthObservation : null,
          solution_state: usesAdaptive2027 ? solutionState : null
        })
      });
      setPreview(result);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Не удалось рассчитать внесение");
    } finally { setState("idle"); }
  }

  async function startRefill() {
    if (!preview?.refill_id) return;
    setState("saving"); setError("");
    try {
      const result = await api<{ status: "started" }>(`/api/applications/refills/${preview.refill_id}/start`, { method: "POST" });
      setPreview({ ...preview, refill_status: result.status });
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Не удалось зафиксировать начало заправки");
    } finally { setState("idle"); }
  }

  async function save() {
    setState("saving"); setError("");
    try {
      await api("/api/applications", {
        method: "POST",
        headers: { "Idempotency-Key": idempotencyKey },
        body: JSON.stringify({
          operation_date: date,
          remainder_liters: remainder,
          work_volume_liters: 90,
          comment,
          calculation_sha256: preview?.calculation_sha256,
          refill_id: preview?.refill_id,
          growth_observation: usesAdaptive2027 ? growthObservation : null,
          solution_state: usesAdaptive2027 ? solutionState : null
        })
      });
      setSuccess("Внесение сохранено. Журнал и склад обновлены.");
      setPreview(null); setRemainder(""); setComment(""); setIdempotencyKey(crypto.randomUUID());
      onSaved();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Запись не сохранена");
    } finally { setState("idle"); }
  }

  return (
    <div className="page-stack compact">
      <section className="page-heading"><div><p className="eyebrow">Рабочий сценарий</p><h2>Новое внесение</h2></div></section>
      <button type="button" className="recipe-inline-card" onClick={onOpenRecipes}>
        <FlaskConical /><span><small>Рецептура на выбранную дату</small><strong>{activeRecipe?.label ?? "Не найдена"}</strong><em>{activeRecipe ? `${activeRecipe.zones.map((zone) => zone.zone_number).join(", ")} зоны · открыть состав` : "Проверьте период действия"}</em></span><ChevronRight />
      </button>
      <form className="panel form-panel" onSubmit={calculate}>
        <label><span>Дата</span><input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPreview(null); }} required /></label>
        <label><span>Остаток в бочке <small>измерьте перед расчётом</small></span><div className="input-unit"><input inputMode="decimal" type="number" min="0" max="90" step="0.1" value={remainder} onChange={(e) => { setRemainder(e.target.value); setPreview(null); }} placeholder="Введите измеренный остаток" required /><b>л</b></div></label>
        <div className="calculation-strip"><span>Рабочий объём</span><strong>90 л</strong><span>Будет долито</span><strong>{remainder === "" ? "—" : `${Math.max(0, 90 - Number(remainder)).toLocaleString("ru-RU")} л`}</strong></div>
        {usesAdaptive2027 && <div className="refill-observations">
          <label><span>Как растёт газон сейчас</span><select value={growthObservation} onChange={(event) => { setGrowthObservation(event.target.value); setPreview(null); }}><option value="weak">Слабо — нужно усиление</option><option value="normal">Нормально</option><option value="excess_or_damage">Есть избыток или повреждение</option><option value="no_growth">Рост остановился</option></select></label>
          <label><span>Раствор в бочке</span><select value={solutionState} onChange={(event) => { setSolutionState(event.target.value); setPreview(null); }}><option value="clear">Прозрачный, без осадка</option><option value="sediment">Есть осадок или помутнение</option></select></label>
          <p>Два коротких наблюдения нужны для безопасного повышения уровня. Массы элементов вручную вводить не требуется.</p>
        </div>}
        <label><span>Комментарий <small>необязательно</small></span><textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Что заметили на газоне" /></label>
        {error && <div className="notice error" role="alert">{error}</div>}
        {success && <div className="notice success" role="status">{success}</div>}
        <button type="submit" className="primary-button sticky-action" disabled={state !== "idle" || remainder === ""} aria-busy={state === "preview"}>{state === "preview" ? <><LoaderCircle className="spin" />Рассчитываю…</> : "Рассчитать состав"}</button>
      </form>
      {preview && (
        <section className="panel preview-panel" ref={previewRef} tabIndex={-1} role="region" aria-label="Результат расчёта состава">
          <div className="panel-title"><div><p className="eyebrow">Проверка перед сохранением</p><h3>{preview.recipe_name}</h3></div><button className="icon-button" onClick={() => setPreview(null)}><X /></button></div>
          <div className={`adaptive-result ${preview.calculation_mode}`}>
            <div>
              <Leaf />
              <span>
                <strong>{preview.calculation_mode.startsWith("adaptive") ? preview.calculation_snapshot.phase_label : "Опубликованная рецептура"}</strong>
                <small>{preview.calculation_mode.startsWith("adaptive")
                  ? `Расчёт до ${formatDate(preview.calculation_snapshot.horizon_end!)} · темп ${formatNumber(preview.calculation_snapshot.pace_liters_per_day!, 1)} л/день`
                  : preview.calculation_snapshot.reason}</small>
              </span>
            </div>
            {preview.calculation_mode.startsWith("adaptive") && <p>{preview.calculation_mode === "adaptive_degraded" ? "Hydrawise временно недоступен: используется ограниченный аварийный расчёт без компенсации долга и повышения уровня." : "Измеренный остаток и фактическая работа Hydrawise уже учтены. Компенсация ограничена текущей фазой и безопасным суточным пределом."}</p>}
          </div>
          {preview.calculation_snapshot.safety_policy?.version && <div className="calculation-strip"><span>Регламент компенсации</span><strong>{preview.calculation_snapshot.safety_policy.version}</strong><span>Азотный долг</span><strong>{preview.calculation_snapshot.irrigation_support?.catchup_allowed ? `${preview.calculation_snapshot.safety_policy.recoverable_debt_days ?? 0} дней` : "не догоняется"}</strong></div>}
          {preview.calculation_snapshot.crosses_phase_boundary && <div className="notice warning">Бак пересекает сезонную границу. Старый долг не перенесён, состав ограничен материалами обеих фаз.</div>}
          {!!preview.calculation_snapshot.warnings?.length && <details className="notice warning adaptive-warnings" open={preview.calculation_snapshot.safety_policy?.single_tank_phosphate_magnesium_status === "visual_clarity_required"}>
            <summary>Сработали ограничения регламента компенсации</summary>
            <p>Ничего пересчитывать не нужно. Система продолжит коррекцию автоматически.</p>
            <ul>{preview.calculation_snapshot.warnings.filter((warning) => !warning.startsWith("Бак пересекает")).map((warning) => <li key={warning}>{warning}</li>)}</ul>
          </details>}
          <MaterialAdjustmentList calculation={preview.calculation_snapshot} />
          <div className="dose-total"><Droplets /><div><span>Объём новой воды</span><strong>{formatNumber(preview.refill_liters, 1)} л</strong></div></div>
          <div className="zone-summary"><span>Зоны внесения</span><div>{preview.zones.map((zone) => <b key={zone.id}>{zone.zone_number}</b>)}</div><small>Зоны 1 и 2 исключены из программы удобрений.</small></div>
          <div className="dose-list">{preview.lines.length ? preview.lines.map((line) => <div key={line.legacy_id}><span>{line.name}</span><strong>{formatNumber(line.grams, 1)} г</strong></div>) : <div><span>Удобрения в эту заправку</span><strong>не требуются</strong></div>}</div>
          <div className="preview-footer"><span>Стоимость</span><strong>{preview.total_cost ? `${formatNumber(preview.total_cost, 2)} ₽` : "Нет цены"}</strong></div>
          {preview.refill_id && preview.refill_status === "calculated" ? <button className="primary-button" onClick={startRefill} disabled={state === "saving"}>{state === "saving" ? <><LoaderCircle className="spin" />Фиксирую…</> : "Начать заправку"}</button> : <button className="primary-button" onClick={save} disabled={state === "saving"}>{state === "saving" ? <><LoaderCircle className="spin" />Сохраняю…</> : "Подтвердить внесение"}</button>}
          <p className="save-hint">{preview.refill_id && preview.refill_status === "calculated" ? "После нажатия состав фиксируется: можно начинать смешивание по рассчитанным массам." : "Запись, движения склада, наблюдение и аудит сохранятся одной операцией."}</p>
        </section>
      )}
    </div>
  );
}

function ApplicationDetailDialog({
  detail,
  state,
  error,
  user,
  onClose,
  onReverse
}: {
  detail: ApplicationDetail | null;
  state: "loading" | "ready" | "error";
  error: string;
  user: User;
  onClose: () => void;
  onReverse: (comment: string) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [showReverse, setShowReverse] = useState(false);
  const [reverseComment, setReverseComment] = useState("");
  const [reversing, setReversing] = useState(false);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") { onClose(); return; }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      ));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  async function submitReverse(event: FormEvent) {
    event.preventDefault();
    setReversing(true);
    try {
      await onReverse(reverseComment);
      setShowReverse(false);
      setReverseComment("");
    } finally { setReversing(false); }
  }

  return (
    <div className="detail-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <section className="application-detail" role="dialog" aria-modal="true" aria-labelledby="application-detail-title" ref={dialogRef}>
        <header className="application-detail-header">
          <div><p className="eyebrow">Подробности внесения</p><h2 id="application-detail-title">{detail ? `Долив ${formatNumber(detail.refill_liters)} л` : "Открываю запись"}</h2></div>
          <button ref={closeRef} type="button" className="icon-button" onClick={onClose} aria-label="Закрыть подробности"><X /></button>
        </header>
        {state === "loading" ? <Loading label="Загружаю состав и стоимость…" /> : state === "error" ? <div className="notice error" role="alert">{error}</div> : detail && <div className="application-detail-content">
          <div className="application-detail-status">
            <span className={`status-pill ${detail.status === "reversed" ? "waiting" : ""}`}>{detail.status === "reversed" ? "Отменено" : "Подтверждено"}</span>
            <span><Clock3 />Зафиксировано {formatDateTime(detail.recorded_at)} по Москве</span>
          </div>
          <section className="application-facts" aria-label="Объёмы внесения">
            <article><span>Остаток в бочке</span><strong>{formatNumber(detail.remainder_liters, 1)} л</strong></article>
            <article><span>Рабочий объём</span><strong>{formatNumber(detail.work_volume_liters, 1)} л</strong></article>
            <article className="accent"><span>Долито воды</span><strong>{formatNumber(detail.refill_liters, 1)} л</strong></article>
            <article><span>Стоимость</span><strong>{detail.total_cost !== null ? `${formatNumber(detail.total_cost, 2)} ₽` : "Не заполнена"}</strong></article>
          </section>
          {detail.calculation && <section className="application-detail-section">
            <div className="detail-section-title"><div><p className="eyebrow">Расчёт состава</p><h3>{detail.calculation.mode.startsWith("adaptive") ? detail.calculation.phase_label : "Опубликованная рецептура"}</h3></div><Leaf /></div>
            <div className={`adaptive-result ${detail.calculation.mode}`}>
              <p>{detail.calculation.mode.startsWith("adaptive")
                ? `Состав рассчитан по фактическим поливам, измеренному остатку и сезонной цели${detail.calculation.horizon_end ? ` до ${formatDate(detail.calculation.horizon_end)}` : ""}.`
                : detail.calculation.reason ?? "Использована опубликованная рецептура."}</p>
              {detail.calculation.warnings?.map((warning) => <small key={warning}>{warning}</small>)}
            </div>
            <MaterialAdjustmentList calculation={detail.calculation} />
          </section>}
          <section className="application-detail-section">
            <div className="detail-section-title"><div><p className="eyebrow">Состав</p><h3>Что добавили</h3></div><span>{detail.lines.length} поз.</span></div>
            <div className="application-materials">
              {detail.lines.map((line) => <article key={line.nomenclature_id}>
                <div><strong>{line.name}</strong><small>{line.legacy_id}</small></div>
                <span><small>Количество</small><b>{formatNumber(line.grams, 1)} г</b></span>
                <span><small>Цена за грамм</small><b>{line.price_per_gram !== null ? `${formatNumber(line.price_per_gram, 4)} ₽` : "—"}</b></span>
                <span><small>Стоимость</small><b>{line.cost !== null ? `${formatNumber(line.cost, 2)} ₽` : "—"}</b></span>
              </article>)}
            </div>
          </section>
          <section className="application-detail-grid">
            <article><span>Зоны внесения</span><div className="detail-zone-list">{detail.zones.map((zone) => <b key={zone.id} title={zone.name}>{zone.zone_number}</b>)}</div><small>Зоны 1 и 2 не участвуют в программе удобрений.</small></article>
            <article><span>Автор записи</span><strong><UserRound />{detail.author.label}</strong><small>{detail.author.source_label}</small></article>
            <article><span>Дата операции</span><strong>{formatDate(detail.operation_date)}</strong><small>Источник: {detail.source_label}</small></article>
            <article><span>Рецептура</span><strong><FlaskConical />{detail.recipe?.label ?? "Историческая запись"}</strong><small>{detail.recipe?.snapshot_sha256 ? `Снимок ${detail.recipe.snapshot_sha256.slice(0, 12)}…` : "Фактический состав сохранён в строках"}</small></article>
            <article><span>ID записи</span><strong className="detail-id">{detail.legacy_id ?? detail.id}</strong><small>Сохраняется для аудита и сверки.</small></article>
          </section>
          <section className="application-comment">
            <span>Комментарий</span>
            <p>{detail.comment || "Комментарий не был добавлен."}</p>
          </section>
          {detail.reversal && <section className="reversal-summary"><strong>Запись отменена {formatDateTime(detail.reversal.reversed_at)}</strong><p>{detail.reversal.comment}</p>{detail.reversal.author_label && <small>Отменил: {detail.reversal.author_label}</small>}</section>}
          {can(user, "history.write") && detail.status === "committed" && (showReverse ? <form className="reversal-form" onSubmit={submitReverse}><label><span>Причина отмены</span><textarea minLength={3} value={reverseComment} onChange={(event) => setReverseComment(event.target.value)} required autoFocus /></label><div><button type="button" className="secondary-button" onClick={() => setShowReverse(false)}>Не отменять</button><button className="danger-button" disabled={reversing}>{reversing ? "Отменяю…" : "Подтвердить отмену"}</button></div></form> : <button type="button" className="text-button danger detail-reverse" onClick={() => setShowReverse(true)}><RotateCcw />Отменить без удаления истории</button>)}
        </div>}
      </section>
    </div>
  );
}

function Journal({ rows, loading, user, onSaved }: { rows: Application[]; loading: boolean; user: User; onSaved: () => Promise<void> }) {
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState<ApplicationDetail | null>(null);
  const [detailState, setDetailState] = useState<"loading" | "ready" | "error">("loading");
  const [detailError, setDetailError] = useState("");
  const [success, setSuccess] = useState("");
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const loadDetail = useCallback(async (id: string) => {
    setDetailState("loading"); setDetailError("");
    try {
      setDetail(await api<ApplicationDetail>(`/api/applications/${id}`));
      setDetailState("ready");
    } catch (reason) {
      setDetailError(reason instanceof ApiError ? reason.message : "Не удалось открыть запись");
      setDetailState("error");
    }
  }, []);

  function openDetail(id: string, trigger: HTMLButtonElement) {
    triggerRef.current = trigger;
    setSelectedId(id); setDetail(null); setSuccess("");
    void loadDetail(id);
  }

  const closeDetail = useCallback(() => {
    setSelectedId(""); setDetail(null);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }, []);

  async function reverse(comment: string) {
    if (!selectedId) return;
    try {
      await api(`/api/applications/${selectedId}/reverse`, { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify({ comment }) });
      setSuccess("Внесение отменено обратными движениями. История сохранена.");
      await onSaved();
      await loadDetail(selectedId);
    } catch (reason) {
      const message = reason instanceof ApiError ? reason.message : "Внесение не отменено";
      setDetailError(message); setDetailState("error");
      throw reason;
    }
  }

  return <div className="page-stack">
    <section className="page-heading"><div><p className="eyebrow">История</p><h2>Журнал операций</h2></div></section>
    {success && <div className="notice success" role="status">{success}</div>}
    {loading ? <Loading label="Читаю журнал…" /> : <section className="timeline">{rows.map((row) => <article key={row.id} className="timeline-item">
      <div className="timeline-date"><strong>{formatDate(row.operation_date)}</strong><span>{formatDateTime(row.recorded_at).split(", ").pop()}</span></div>
      <div className="timeline-card"><div><span className={`status-pill ${row.status === "reversed" ? "waiting" : ""}`}>{row.status === "reversed" ? "Отменено" : "Подтверждено"}</span><strong>Долив {formatNumber(row.refill_liters)} л</strong><small>{row.lines} материалов · зоны {row.zones.map((zone) => zone.zone_number).join(", ")} · {row.total_cost !== null ? `${formatNumber(row.total_cost, 2)} ₽` : "стоимость не заполнена"}</small></div>{row.comment && <p>{row.comment}</p>}<div className="timeline-footer"><span><UserRound />{row.author_label}</span><button type="button" className="detail-link" onClick={(event) => openDetail(row.id, event.currentTarget)}>Смотреть детали<ChevronRight /></button></div></div>
    </article>)}</section>}
    {selectedId && <ApplicationDetailDialog detail={detail} state={detailState} error={detailError} user={user} onClose={closeDetail} onReverse={reverse} />}
  </div>;
}

type StockTab = "balances" | "purchase" | "inventory" | "history";

function StockPage({
  items,
  documents,
  user,
  loading,
  onSaved
}: {
  items: StockItem[];
  documents: StockDocument[];
  user: User;
  loading: boolean;
  onSaved: () => Promise<void>;
}) {
  const [tab, setTab] = useState<StockTab>("balances");
  const [purchaseLines, setPurchaseLines] = useState([{ nomenclature_id: "", quantity_grams: "", total_cost: "" }]);
  const [purchaseMeta, setPurchaseMeta] = useState({ shop: "", order_number: "", producer: "", comment: "" });
  const [purchaseKey, setPurchaseKey] = useState(crypto.randomUUID());
  const [inventoryActual, setInventoryActual] = useState<Record<string, string>>({});
  const [inventoryComment, setInventoryComment] = useState("");
  const [inventoryKey, setInventoryKey] = useState(crypto.randomUUID());
  const [reversingId, setReversingId] = useState("");
  const [reversalComment, setReversalComment] = useState("");
  const [reversalKey, setReversalKey] = useState(crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setInventoryActual(Object.fromEntries(items.map((item) => [item.id, item.balance_grams])));
  }, [items]);

  function updatePurchaseLine(index: number, key: "nomenclature_id" | "quantity_grams" | "total_cost", value: string) {
    setPurchaseLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, [key]: value } : line));
  }

  async function savePurchase(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError(""); setSuccess("");
    try {
      await api("/api/stock/purchases", {
        method: "POST",
        headers: { "Idempotency-Key": purchaseKey },
        body: JSON.stringify({ operation_date: moscowToday(), lines: purchaseLines, ...purchaseMeta })
      });
      setSuccess("Закупка проведена. Остатки и стоимость обновлены.");
      setPurchaseLines([{ nomenclature_id: "", quantity_grams: "", total_cost: "" }]);
      setPurchaseMeta({ shop: "", order_number: "", producer: "", comment: "" });
      setPurchaseKey(crypto.randomUUID());
      await onSaved();
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "Закупка не сохранена"); }
    finally { setBusy(false); }
  }

  async function saveInventory(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError(""); setSuccess("");
    try {
      await api("/api/stock/inventories", {
        method: "POST",
        headers: { "Idempotency-Key": inventoryKey },
        body: JSON.stringify({
          operation_date: moscowToday(),
          comment: inventoryComment,
          lines: items.map((item) => ({
            nomenclature_id: item.id,
            expected_balance_grams: item.balance_grams,
            actual_balance_grams: inventoryActual[item.id]
          }))
        })
      });
      setSuccess("Инвентаризация проведена. Разницы записаны отдельными движениями.");
      setInventoryComment(""); setInventoryKey(crypto.randomUUID());
      await onSaved();
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "Инвентаризация не сохранена"); }
    finally { setBusy(false); }
  }

  async function reverseDocument(event: FormEvent) {
    event.preventDefault(); if (!reversingId) return;
    setBusy(true); setError(""); setSuccess("");
    try {
      await api(`/api/stock/documents/${reversingId}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": reversalKey },
        body: JSON.stringify({ operation_date: moscowToday(), comment: reversalComment })
      });
      setSuccess("Документ отменён обратными движениями. История сохранена.");
      setReversingId(""); setReversalComment(""); setReversalKey(crypto.randomUUID());
      await onSaved();
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "Документ не отменён"); }
    finally { setBusy(false); }
  }

  const labels = { purchase: "Закупка", inventory: "Инвентаризация", reversal: "Отмена" };
  return <div className="page-stack">
    <section className="page-heading"><div><p className="eyebrow">Учёт материалов</p><h2>Склад</h2></div></section>
    <div className="section-tabs">
      <button className={tab === "balances" ? "active" : ""} onClick={() => setTab("balances")}>Остатки</button>
      {can(user, "stock.write") && <button className={tab === "purchase" ? "active" : ""} onClick={() => setTab("purchase")}><ShoppingCart />Закупка</button>}
      {can(user, "stock.write") && <button className={tab === "inventory" ? "active" : ""} onClick={() => setTab("inventory")}><Scale />Инвентаризация</button>}
      <button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>Документы</button>
    </div>
    {error && <div className="notice error" role="alert">{error}</div>}
    {success && <div className="notice success" role="status">{success}</div>}
    {loading && items.length === 0 ? <Loading label="Сверяю склад…" /> : tab === "balances" ? <section className="stock-cards">{items.map((item) => <article className="stock-card" key={item.id}><span>{item.legacy_id}</span><h3>{item.name}</h3><strong>{formatNumber(item.balance_grams, 1)} <em>г</em></strong><small>{item.balance_cost === null ? "Стоимость не определена" : `${formatNumber(item.balance_cost, 2)} ₽ · ${formatNumber(item.unit_cost ?? 0, 4)} ₽/г`}</small></article>)}</section>
      : tab === "purchase" ? <form className="panel form-panel" onSubmit={savePurchase}><div className="panel-title"><div><p className="eyebrow">Поступление</p><h3>Новая закупка</h3></div><ShoppingCart /></div>{purchaseLines.map((line, index) => <div className="document-line" key={index}><label><span>Материал {index + 1}</span><select aria-label={`Материал закупки ${index + 1}`} value={line.nomenclature_id} onChange={(event) => updatePurchaseLine(index, "nomenclature_id", event.target.value)} required><option value="">Выберите материал</option>{items.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label><span>Количество, г</span><input aria-label={`Количество закупки ${index + 1}`} type="number" min="0.001" step="0.001" value={line.quantity_grams} onChange={(event) => updatePurchaseLine(index, "quantity_grams", event.target.value)} required /></label><label><span>Стоимость, ₽</span><input aria-label={`Стоимость закупки ${index + 1}`} type="number" min="0.01" step="0.01" value={line.total_cost} onChange={(event) => updatePurchaseLine(index, "total_cost", event.target.value)} required /></label>{purchaseLines.length > 1 && <button type="button" className="icon-button" aria-label={`Убрать материал ${index + 1}`} onClick={() => setPurchaseLines((current) => current.filter((_, lineIndex) => lineIndex !== index))}><X /></button>}</div>)}<button type="button" className="secondary-button" onClick={() => setPurchaseLines((current) => [...current, { nomenclature_id: "", quantity_grams: "", total_cost: "" }])}>Добавить материал</button><div className="form-grid"><label><span>Магазин</span><input value={purchaseMeta.shop} onChange={(e) => setPurchaseMeta({ ...purchaseMeta, shop: e.target.value })} /></label><label><span>Номер заказа</span><input value={purchaseMeta.order_number} onChange={(e) => setPurchaseMeta({ ...purchaseMeta, order_number: e.target.value })} /></label><label><span>Производитель</span><input value={purchaseMeta.producer} onChange={(e) => setPurchaseMeta({ ...purchaseMeta, producer: e.target.value })} /></label></div><label><span>Комментарий</span><textarea rows={2} value={purchaseMeta.comment} onChange={(e) => setPurchaseMeta({ ...purchaseMeta, comment: e.target.value })} /></label><button className="primary-button" disabled={busy}>{busy ? <><LoaderCircle className="spin" />Провожу…</> : "Провести закупку"}</button><p className="save-hint">Документ и все движения сохраняются одной транзакцией.</p></form>
      : tab === "inventory" ? <form className="panel form-panel" onSubmit={saveInventory}><div className="panel-title"><div><p className="eyebrow">Контроль остатков</p><h3>Инвентаризация</h3></div><Scale /></div><div className="inventory-table">{items.map((item) => <label key={item.id}><span>{item.name}<small>Расчёт: {formatNumber(item.balance_grams, 1)} г</small></span><div className="input-unit"><input aria-label={`Фактический остаток ${item.name}`} type="number" min="0" step="0.001" value={inventoryActual[item.id] ?? ""} onChange={(event) => setInventoryActual({ ...inventoryActual, [item.id]: event.target.value })} required /><b>г</b></div></label>)}</div><label><span>Комментарий</span><textarea rows={2} value={inventoryComment} onChange={(e) => setInventoryComment(e.target.value)} placeholder="Например: контрольное взвешивание" /></label><div className="notice neutral">Если склад изменился после открытия формы, система ничего не перезапишет и предложит обновить данные.</div><button className="primary-button" disabled={busy}>{busy ? <><LoaderCircle className="spin" />Сверяю…</> : "Провести инвентаризацию"}</button></form>
      : <section className="document-list">{documents.map((document) => <article className="document-card" key={document.id}><div className="document-card-heading"><div><span className={`status-pill ${document.status === "reversed" ? "waiting" : ""}`}>{document.status === "reversed" ? "Отменён" : labels[document.document_type]}</span><h3>{formatDate(document.operation_date)}</h3></div><strong>{formatNumber(document.total_cost, 2)} ₽</strong></div><div className="document-items">{document.lines.filter((line) => Number(line.quantity_grams) !== 0).map((line) => <span key={line.nomenclature_id}>{line.name}<b>{Number(line.quantity_grams) > 0 ? "+" : ""}{formatNumber(line.quantity_grams, 1)} г</b></span>)}</div>{document.comment && <p>{document.comment}</p>}{can(user, "stock.write") && document.status === "committed" && document.document_type !== "reversal" && (reversingId === document.id ? <form className="reversal-form" onSubmit={reverseDocument}><label><span>Причина отмены</span><textarea minLength={3} required value={reversalComment} onChange={(e) => setReversalComment(e.target.value)} /></label><div><button type="button" className="secondary-button" onClick={() => setReversingId("")}>Не отменять</button><button className="danger-button" disabled={busy}>Подтвердить отмену</button></div></form> : <button className="text-button danger" onClick={() => { setReversingId(document.id); setReversalComment(""); setReversalKey(crypto.randomUUID()); }}><RotateCcw />Отменить без удаления истории</button>)}</article>)}</section>}
  </div>;
}

function ZonePhotoEditor({ zone, onClose, onSaved }: { zone: Zone; onClose: () => void; onSaved: () => Promise<void> }) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [draft, setDraft] = useState<ZonePhotoVersion | null>(null);
  const [versions, setVersions] = useState<ZonePhotoVersion[]>([]);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedPixels, setCroppedPixels] = useState<Area | null>(null);
  const [state, setState] = useState<"idle" | "uploading" | "editing" | "saving" | "restoring">("idle");
  const [error, setError] = useState("");

  const loadVersions = useCallback(async () => {
    try { setVersions(await api<ZonePhotoVersion[]>(`/api/zones/${zone.id}/photos`)); }
    catch { setVersions([]); }
  }, [zone.id]);

  useEffect(() => {
    void loadVersions();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const keydown = (event: KeyboardEvent) => { if (event.key === "Escape" && state !== "saving") onClose(); };
    document.addEventListener("keydown", keydown);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener("keydown", keydown); };
  }, [loadVersions, onClose, state]);

  async function upload(file: File | undefined) {
    if (!file) return;
    setState("uploading"); setError("");
    const form = new FormData(); form.append("photo", file);
    try {
      const prepared = await api<ZonePhotoVersion>(`/api/zones/${zone.id}/photos/drafts`, { method: "POST", body: form });
      setDraft(prepared); setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); setState("editing");
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Не удалось подготовить фотографию"); setState("idle");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  }

  async function redraft(version: ZonePhotoVersion) {
    setState("uploading"); setError("");
    try {
      const prepared = await api<ZonePhotoVersion>(`/api/zones/${zone.id}/photos/${version.id}/redraft`, { method: "POST" });
      setDraft(prepared); setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); setState("editing");
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Не удалось открыть исходник"); setState("idle");
    }
  }

  async function save() {
    if (!draft || !croppedPixels) return;
    setState("saving"); setError("");
    try {
      await api(`/api/zones/${zone.id}/photos/${draft.id}/finalize`, {
        method: "POST",
        body: JSON.stringify({ crop: croppedPixels, rotation })
      });
      await onSaved(); onClose();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Фотография не сохранена"); setState("editing");
    }
  }

  async function restore(version: ZonePhotoVersion) {
    if (version.status === "active") return;
    setState("restoring"); setError("");
    try {
      await api(`/api/zones/${zone.id}/photos/${version.id}/activate`, { method: "POST" });
      await onSaved(); await loadVersions();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Предыдущая фотография не восстановлена");
    } finally { setState("idle"); }
  }

  const preview = draft?.urls.preview;
  return <div className="photo-editor-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target && state !== "saving") onClose(); }}>
    <section className="photo-editor" role="dialog" aria-modal="true" aria-labelledby="zone-photo-title">
      <header><div><p className="eyebrow">Зона {zone.zone_number}</p><h2 id="zone-photo-title">Фотография зоны</h2></div><button ref={closeRef} className="icon-button" onClick={onClose} aria-label="Закрыть редактор"><X /></button></header>
      <div className="photo-editor-content">
        {error && <div className="notice error" role="alert">{error}</div>}
        {state === "uploading" ? <Loading label="Подготавливаю фотографию…" /> : draft && preview ? <>
          <div className="crop-stage"><Cropper image={preview} crop={crop} zoom={zoom} rotation={rotation} aspect={16 / 9} onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation} onCropComplete={(_area, pixels) => setCroppedPixels(pixels)} showGrid /></div>
          <div className="crop-controls"><label><span>Масштаб</span><input aria-label="Масштаб фотографии" type="range" min="1" max="3" step="0.01" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} /></label><div><button type="button" className="secondary-button" onClick={() => setRotation((value) => value - 90)}><RotateCcw />Повернуть</button><button type="button" className="secondary-button" onClick={() => setRotation((value) => value + 90)}><RotateCw />Повернуть</button></div></div>
          <div className="photo-editor-actions"><button type="button" className="secondary-button" onClick={() => { setDraft(null); setState("idle"); }}>Выбрать другую</button><button type="button" className="primary-button" onClick={save} disabled={state === "saving" || !croppedPixels}>{state === "saving" ? <><LoaderCircle className="spin" />Обрабатываю…</> : <><Check />Сохранить кадр</>}</button></div>
        </> : <>
          <div className="photo-upload-current"><img src={zone.photo_path} alt={`Текущая фотография зоны ${zone.zone_number}`} /><div><h3>{zone.name}</h3><p>Выберите готовую фотографию или снимите новую. Исходник сохранится — кадр можно будет заменить позже.</p></div></div>
          <div className="photo-source-actions"><button className="primary-button" onClick={() => fileRef.current?.click()}><ImagePlus />Выбрать фотографию</button><button className="secondary-button" onClick={() => cameraRef.current?.click()}><Camera />Открыть камеру</button>{versions.find((version) => version.status === "active" && version.source_kind === "upload") && <button className="secondary-button" onClick={() => void redraft(versions.find((version) => version.status === "active")!)}><RotateCcw />Изменить кадр</button>}</div>
          <input ref={fileRef} className="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" onChange={(event) => void upload(event.target.files?.[0])} />
          <input ref={cameraRef} className="visually-hidden" type="file" accept="image/*" capture="environment" onChange={(event) => void upload(event.target.files?.[0])} />
          {versions.length > 1 && <section className="photo-history"><div><p className="eyebrow">История</p><h3>Предыдущие варианты</h3></div><div>{versions.map((version) => { const src = version.urls["400"] ?? version.urls["800"]; return <article key={version.id}>{src && <img src={src} alt="" />}<span><strong>{version.status === "active" ? "Текущая" : "Предыдущая"}</strong><small>{version.author_label ?? "Пользователь"} · {formatDateTime(version.created_at)}</small></span>{version.status === "active" ? <b className="status-pill">Активна</b> : <button className="text-button" disabled={state === "restoring"} onClick={() => void restore(version)}>Вернуть</button>}</article>; })}</div></section>}
        </>}
      </div>
    </section>
  </div>;
}

const irrigationStateLabels: Record<IrrigationCommand["state"], string> = {
  requested: "Готовлю запуск…",
  sending: "Запускаю…",
  reconciling: "Проверяю состояние…",
  running: "Полив идёт",
  stopping: "Останавливаю…",
  completed: "Полив завершён",
  stopped: "Полив остановлен",
  rejected: "Не запущено",
  failed_safety_stop: "Остановлено для безопасности"
};

const openIrrigationStates = new Set<IrrigationCommand["state"]>([
  "requested", "sending", "reconciling", "running", "stopping"
]);

function ZoneControlPanel({
  zone,
  user,
  onClose,
  onEditPhoto,
  onStatusChange
}: {
  zone: Zone;
  user: User;
  onClose: () => void;
  onEditPhoto: () => void;
  onStatusChange: (commands: IrrigationCommand[]) => void;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [status, setStatus] = useState<IrrigationControlStatus | null>(null);
  const [minutes, setMinutes] = useState(10);
  const [ignoreRain, setIgnoreRain] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [now, setNow] = useState(() => Date.now());

  const loadStatus = useCallback(async () => {
    try {
      const result = await api<IrrigationControlStatus>("/api/hydrawise/control/status");
      setStatus(result);
      onStatusChange(result.active_commands);
    }
    catch (reason) { setError(reason instanceof ApiError ? reason.message : "Не удалось проверить Hydrawise"); }
  }, [onStatusChange]);

  useEffect(() => {
    void loadStatus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    const keydown = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", keydown);
    const poll = window.setInterval(() => void loadStatus(), 5000);
    const clock = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown);
      window.clearInterval(poll);
      window.clearInterval(clock);
    };
  }, [busy, loadStatus, onClose]);

  const command = status?.active_commands.find((item) => item.zone_id === zone.id) ?? null;
  const otherCommand = status?.active_commands.find((item) => item.zone_id !== zone.id) ?? null;
  const remaining = command?.expected_end_at
    ? Math.max(0, Math.ceil((new Date(command.expected_end_at).getTime() - now) / 1000))
    : status?.controller?.remaining_seconds ?? null;
  const unavailable = !status?.available || zone.hydrawise_mapping_state !== "mapped";

  async function start() {
    if (!confirming) { setConfirming(true); return; }
    setBusy(true); setError("");
    try {
      await api<IrrigationCommand>("/api/hydrawise/control/commands", {
        method: "POST",
        body: JSON.stringify({
          zone_id: zone.id,
          idempotency_key: idempotencyKey,
          duration_seconds: minutes * 60,
          ignore_rain_sensor: ignoreRain,
          queue_on_conflict: true
        })
      });
      setConfirming(false);
      await loadStatus();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Команда не отправлена");
    } finally { setBusy(false); }
  }

  async function stop() {
    if (!command) return;
    setBusy(true); setError("");
    try {
      await api(`/api/hydrawise/control/commands/${command.id}/stop`, { method: "POST" });
      await loadStatus();
      setIdempotencyKey(crypto.randomUUID());
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Не удалось подтвердить остановку");
    } finally { setBusy(false); }
  }

  return <div className="photo-editor-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target && !busy) onClose(); }}>
    <section className="photo-editor zone-control" role="dialog" aria-modal="true" aria-labelledby="zone-control-title">
      <header><div><p className="eyebrow">Зона {zone.zone_number}</p><h2 id="zone-control-title">Ручной полив</h2></div><button ref={closeRef} className="icon-button" onClick={onClose} aria-label="Закрыть управление"><X /></button></header>
      <div className="photo-editor-content">
        <div className="control-zone-summary"><div className="control-zone-photo"><img src={zone.photo_path} srcSet={`${zone.photo_srcset["400"]} 400w, ${zone.photo_srcset["800"]} 800w, ${zone.photo_srcset["1600"]} 1600w`} sizes="(max-width: 640px) 112px, 160px" alt={`Зона ${zone.zone_number}: ${zone.name}`} />{can(user, "zone.photo.write") && <button type="button" className="control-photo-edit" onClick={onEditPhoto} disabled={busy} aria-label={`Изменить фотографию зоны ${zone.zone_number}`}><Camera /></button>}</div><div><h3>{zone.name}</h3><p>{zone.irrigation_type === "drip" ? "Капельный полив" : "Газонный полив"} · Зона {zone.zone_number}</p></div></div>
        {!status ? <Loading label="Проверяю контроллер…" /> : !status.available ? <div className="notice neutral"><ShieldCheck /><div><strong>Управление пока закрыто безопасно</strong><span>{status.message}</span></div></div> : zone.hydrawise_mapping_state !== "mapped" ? <div className="notice error">Эта зона ещё не связана с Hydrawise, поэтому запуск недоступен.</div> : null}
        {error && <div className="notice error" role="alert">{error}</div>}
        {command ? <section className={`active-irrigation ${command.state}`} aria-live="polite"><div><Droplets /><span><strong>{irrigationStateLabels[command.state]}</strong><small>{remaining !== null && command.state === "running" ? `Осталось ${Math.ceil(remaining / 60)} мин` : command.error_message ?? `Запрошено ${command.duration_seconds / 60} мин`}</small></span></div>{["running", "reconciling", "sending"].includes(command.state) && <button className="danger-button" onClick={stop} disabled={busy || !status?.supports.zone_stop}>{busy ? <LoaderCircle className="spin" /> : <Square />}Остановить</button>}</section> : <>
          {otherCommand && <div className="notice neutral">Сейчас обрабатывается команда для зоны {otherCommand.zone_number}. Новый запуск станет доступен после подтверждения её результата.</div>}
          {!otherCommand && status?.controller?.schedule_conflict && <div className="notice neutral">Hydrawise сейчас поливает {status.controller.active_zone_number ? `зону ${status.controller.active_zone_number}` : "другую зону"}{status.controller.remaining_seconds !== null ? ` — осталось около ${Math.max(1, Math.ceil(status.controller.remaining_seconds / 60))} мин` : ""}. Новый запуск будет поставлен следом в очередь.</div>}
          <section className="control-duration"><div><p className="eyebrow">Длительность</p><h3>{minutes} минут</h3></div><div className="duration-presets">{[5, 10, 15, 20, 30].map((value) => <button type="button" key={value} className={minutes === value ? "active" : ""} onClick={() => { setMinutes(value); setConfirming(false); }}>{value}</button>)}</div><label><span>Точное время, минут</span><input type="number" min="1" max="120" value={minutes} onChange={(event) => { setMinutes(Math.min(120, Math.max(1, Number(event.target.value) || 1))); setConfirming(false); }} /></label></section>
          <label className="rain-override"><input type="checkbox" checked={ignoreRain} onChange={(event) => { setIgnoreRain(event.target.checked); setConfirming(false); }} /><span><strong>Игнорировать датчик дождя</strong><small>Включено по умолчанию только для этого ручного запуска. Автоматические программы Hydrawise не меняются.</small></span></label>
          {status?.controller?.rain_sensor_active && <div className="notice neutral">Датчик дождя сейчас активен. {ignoreRain ? "Этот ручной запуск его проигнорирует." : "Запуск будет безопасно отклонён."}</div>}
          {confirming && <div className="control-confirm" role="status"><strong>Подтвердите запуск</strong><span>Зона {zone.zone_number} · {minutes} мин · датчик дождя {ignoreRain ? "игнорируется" : "учитывается"}{status?.controller?.schedule_conflict ? " · после текущего полива" : ""}</span></div>}
          <button className="primary-button control-start" onClick={start} disabled={busy || unavailable || Boolean(otherCommand)}>{busy ? <><LoaderCircle className="spin" />Отправляю и проверяю…</> : confirming ? <><Play />Подтвердить запуск</> : <><Play />Запустить зону</>}</button>
        </>}
      </div>
    </section>
  </div>;
}

function ZonesPage({ zones, loading, user, onSaved }: { zones: Zone[]; loading: boolean; user: User; onSaved: () => Promise<void> }) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [controllingId, setControllingId] = useState<string | null>(null);
  const [activeCommands, setActiveCommands] = useState<IrrigationCommand[]>([]);
  const editing = zones.find((zone) => zone.id === editingId) ?? null;
  const controlling = zones.find((zone) => zone.id === controllingId) ?? null;
  const handleStatusChange = useCallback((commands: IrrigationCommand[]) => {
    setActiveCommands(commands.filter((command) => openIrrigationStates.has(command.state)));
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadCommands() {
      try {
        const commands = await api<IrrigationCommand[]>("/api/hydrawise/control/commands?limit=30");
        if (!cancelled) handleStatusChange(commands);
      } catch {
        // The control panel explains provider errors; cards retain their last confirmed state.
      }
    }
    void loadCommands();
    const poll = window.setInterval(() => void loadCommands(), 10_000);
    return () => { cancelled = true; window.clearInterval(poll); };
  }, [handleStatusChange]);

  const closeControl = useCallback(() => setControllingId(null), []);

  const openPhotoEditor = useCallback((zoneId: string) => {
    setControllingId(null);
    setEditingId(zoneId);
  }, []);

  const closePhotoEditor = useCallback(() => {
    setEditingId(null);
    if (editingId) setControllingId(editingId);
  }, [editingId]);

  if (loading && zones.length === 0) return <Loading label="Открываю справочник зон…" />;
  return <div className="page-stack zones-page">
    <section className="page-heading"><div><p className="eyebrow">Участок</p><h2>Зоны полива</h2><p>Нажмите на зону, чтобы открыть ручное управление поливом.</p></div></section>
    <div className="zone-grid">{zones.map((zone) => {
      const command = activeCommands.find((item) => item.zone_id === zone.id) ?? null;
      const remainingMinutes = command?.expected_end_at
        ? Math.max(1, Math.ceil((new Date(command.expected_end_at).getTime() - Date.now()) / 60_000))
        : null;
      const liveLabel = command
        ? command.state === "running" && remainingMinutes !== null
          ? `Полив идёт · ${remainingMinutes} мин`
          : irrigationStateLabels[command.state]
        : null;
      return <article className={`zone-card ${command ? "watering" : ""}`} key={zone.id}>
        <button type="button" className="zone-card-open" onClick={() => setControllingId(zone.id)} aria-label={`Открыть управление поливом зоны ${zone.zone_number}: ${zone.name}${liveLabel ? `. ${liveLabel}` : ""}`} />
        <div className="zone-photo"><img src={zone.photo_path} srcSet={`${zone.photo_srcset["400"]} 400w, ${zone.photo_srcset["800"]} 800w, ${zone.photo_srcset["1600"]} 1600w`} sizes="(max-width: 640px) 45vw, (max-width: 1200px) 45vw, 22vw" alt={`Зона ${zone.zone_number}: ${zone.name}`} />{liveLabel ? <span className="zone-live-status"><Droplets />{liveLabel}</span> : <span className="zone-open-affordance" aria-hidden="true"><Droplets /><ChevronRight /></span>}</div>
        <div className="zone-card-body"><div className="zone-card-heading"><span>Зона {zone.zone_number}</span><span className={`status-pill ${zone.participates_in_fertilization ? "" : "waiting"}`}>{zone.participates_in_fertilization ? "В программе" : "Без удобрений"}</span></div><h3>{zone.name}</h3><p>{zone.irrigation_type === "drip" ? "Капельный полив" : "Газонный полив"}</p><div className="zone-meta"><span>{zone.application_count} внесений</span><span>{zone.hydrawise_mapping_state === "mapped" ? "Hydrawise связан" : "Ожидает связи"}</span></div></div>
      </article>;
    })}</div>
    {editing && can(user, "zone.photo.write") && <ZonePhotoEditor zone={editing} onClose={closePhotoEditor} onSaved={onSaved} />}
    {controlling && <ZoneControlPanel zone={controlling} user={user} onClose={closeControl} onEditPhoto={() => openPhotoEditor(controlling.id)} onStatusChange={handleStatusChange} />}
  </div>;
}

const recipeStatusLabels: Record<RecipeVersion["display_status"], string> = {
  draft: "Черновик",
  scheduled: "Запланирована",
  current: "Применяется сейчас",
  superseded: "Заменена",
  rejected: "Отклонена"
};

function RecipeMonthView({ recipe, month, onMonth }: { recipe: RecipeVersion; month: number; onMonth: (month: number) => void }) {
  const months = [...new Map(recipe.lines.map((line) => [line.month, line.month_name])).entries()].sort((a, b) => a[0] - b[0]);
  const lines = recipe.lines.filter((line) => line.month === month);
  return <>
    <div className="recipe-months" aria-label="Месяцы рецептуры">{months.map(([value, label]) => <button key={value} className={month === value ? "active" : ""} onClick={() => onMonth(value)}>{label}</button>)}</div>
    <div className="recipe-dose-grid">{lines.map((line) => <article key={line.id}><span>{line.name}</span><strong>{formatNumber(line.grams_per_100_liters, 1)} <small>г/100 л</small></strong></article>)}</div>
  </>;
}

function RecipeEditor({ draft, catalog, onReload }: { draft: RecipeVersion; catalog: RecipeCatalog; onReload: () => Promise<void> }) {
  const initialMonth = draft.lines.some((line) => line.month === new Date().getMonth() + 1) ? new Date().getMonth() + 1 : draft.lines[0]?.month ?? 1;
  const [month, setMonth] = useState(initialMonth);
  const [effectiveFrom, setEffectiveFrom] = useState(draft.effective_from);
  const [effectiveTo, setEffectiveTo] = useState(draft.effective_to ?? "");
  const [reason, setReason] = useState(draft.change_reason ?? "");
  const [zoneIds, setZoneIds] = useState(() => draft.zones.map((zone) => zone.id));
  const [lines, setLines] = useState(() => draft.lines.map((line) => ({ ...line })));
  const [validation, setValidation] = useState<string[]>([]);
  const [diff, setDiff] = useState<RecipeDiff[]>([]);
  const [busy, setBusy] = useState<"" | "saving" | "checking" | "publishing" | "rejecting">("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const months = [...new Map(lines.map((line) => [line.month, line.month_name])).entries()].sort((a, b) => a[0] - b[0]);
  const visibleLines = lines.filter((line) => line.month === month);
  const payload = () => ({
    effective_from: effectiveFrom,
    effective_to: effectiveTo || null,
    change_reason: reason,
    zone_ids: zoneIds,
    lines: lines.map((line) => ({ month: line.month, nomenclature_id: line.nomenclature_id, grams_per_100_liters: line.grams_per_100_liters }))
  });

  async function saveDraft(): Promise<boolean> {
    setBusy("saving"); setError(""); setSaved(false); setConfirmPublish(false);
    try {
      const result = await api<RecipeVersion & { validation_errors: string[] }>(`/api/recipes/${draft.id}`, { method: "PUT", body: JSON.stringify(payload()) });
      setValidation(result.validation_errors ?? []); setSaved(true); return true;
    } catch (reasonValue) {
      setError(reasonValue instanceof ApiError ? reasonValue.message : "Черновик не сохранён"); return false;
    } finally { setBusy(""); }
  }

  async function checkDraft() {
    if (!(await saveDraft())) return;
    setBusy("checking"); setError("");
    try {
      const result = await api<{ valid: boolean; errors: string[]; diff: RecipeDiff[] }>(`/api/recipes/${draft.id}/validate`, { method: "POST" });
      setValidation(result.errors); setDiff(result.diff);
    } catch (reasonValue) { setError(reasonValue instanceof ApiError ? reasonValue.message : "Проверка не завершилась"); }
    finally { setBusy(""); }
  }

  async function publish() {
    if (!confirmPublish) { await checkDraft(); setConfirmPublish(true); return; }
    if (!(await saveDraft())) return;
    setBusy("publishing"); setError("");
    try { await api(`/api/recipes/${draft.id}/publish`, { method: "POST" }); await onReload(); }
    catch (reasonValue) { setError(reasonValue instanceof ApiError ? reasonValue.message : "Рецептура не опубликована"); }
    finally { setBusy(""); }
  }

  async function reject() {
    setBusy("rejecting"); setError("");
    try { await api(`/api/recipes/${draft.id}/reject`, { method: "POST", body: JSON.stringify({ reason: rejectReason }) }); await onReload(); }
    catch (reasonValue) { setError(reasonValue instanceof ApiError ? reasonValue.message : "Черновик не отклонён"); }
    finally { setBusy(""); }
  }

  return <section className="panel recipe-editor">
    <div className="panel-title"><div><p className="eyebrow">Редактирование не влияет на расчёты</p><h3>{draft.label} · черновик</h3></div><Save /></div>
    {draft.source === "chatgpt_recommendation" && <div className="notice neutral"><GitCompare /><div><strong>Черновик из рекомендации ChatGPT</strong><span>Рекомендация не применяется автоматически. Проверьте каждую дозировку и только затем опубликуйте.</span></div></div>}
    <div className="recipe-form-grid"><label><span>Начало действия</span><input type="date" value={effectiveFrom} onChange={(event) => { setEffectiveFrom(event.target.value); setSaved(false); }} /></label><label><span>Окончание</span><input type="date" value={effectiveTo} onChange={(event) => { setEffectiveTo(event.target.value); setSaved(false); }} /></label></div>
    <label><span>Причина изменения</span><textarea rows={3} value={reason} onChange={(event) => { setReason(event.target.value); setSaved(false); }} placeholder="Что и почему меняем" /></label>
    <div className="recipe-zones"><span>Зоны внесения</span>{catalog.eligible_zones.map((zone) => <label key={zone.id}><input type="checkbox" checked={zoneIds.includes(zone.id)} onChange={(event) => setZoneIds((current) => event.target.checked ? [...current, zone.id] : current.filter((id) => id !== zone.id))} /><b>{zone.zone_number}</b><small>{zone.name}</small></label>)}</div>
    <div className="recipe-months">{months.map(([value, label]) => <button type="button" key={value} className={month === value ? "active" : ""} onClick={() => setMonth(value)}>{label}</button>)}</div>
    <div className="recipe-line-editor">{visibleLines.map((line) => <label key={line.id}><span>{line.name}<small>г на 100 л</small></span><input type="number" min="0" max="100000" step="0.001" value={line.grams_per_100_liters} onChange={(event) => { setLines((current) => current.map((item) => item.id === line.id ? { ...item, grams_per_100_liters: event.target.value } : item)); setSaved(false); }} /></label>)}</div>
    {validation.length > 0 && <div className="notice error" role="alert"><div><strong>Нужно исправить</strong>{validation.map((item) => <span key={item}>{item}</span>)}</div></div>}
    {diff.length > 0 && <div className="recipe-diff"><strong>Изменения относительно {draft.parent_label}</strong>{diff.map((item) => <span key={`${item.month}-${item.nomenclature_id}`}><b>{item.month_name} · {item.name}</b><em>{item.before ?? "—"} → {item.after ?? "—"} г</em></span>)}</div>}
    {error && <div className="notice error" role="alert">{error}</div>}{saved && <div className="notice success" role="status">Черновик сохранён. Действующая рецептура не изменилась.</div>}
    {confirmPublish && <div className="control-confirm"><strong>Подтвердите публикацию</strong><span>{draft.label} начнёт применяться с {formatDate(effectiveFrom)}. Предыдущая версия сохранится в истории.</span></div>}
    <div className="recipe-editor-actions"><button className="secondary-button" onClick={checkDraft} disabled={Boolean(busy)}>{busy === "checking" ? <LoaderCircle className="spin" /> : <GitCompare />}Проверить изменения</button><button className="primary-button" onClick={publish} disabled={Boolean(busy)}>{busy === "publishing" ? <><LoaderCircle className="spin" />Публикую…</> : confirmPublish ? "Подтвердить публикацию" : "Сохранить и опубликовать"}</button></div>
    <div className="recipe-reject"><input value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} placeholder="Причина отклонения черновика" /><button className="text-button" onClick={reject} disabled={rejectReason.trim().length < 5 || Boolean(busy)}>Отклонить</button></div>
  </section>;
}

function RecipesPage() {
  const [catalog, setCatalog] = useState<RecipeCatalog | null>(null);
  const [month, setMonth] = useState(() => Number(new Intl.DateTimeFormat("en", { timeZone: "Europe/Moscow", month: "numeric" }).format(new Date())));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [selected, setSelected] = useState<(RecipeVersion & { diff?: RecipeDiff[] }) | null>(null);

  const load = useCallback(async () => {
    setError("");
    try { setCatalog(await api<RecipeCatalog>("/api/recipes")); }
    catch (reason) { setError(reason instanceof ApiError ? reason.message : "Рецептуры не загрузились"); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function createDraft(source: "manual" | "chatgpt_recommendation") {
    setBusy(true); setError("");
    try {
      await api("/api/recipes/drafts", { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify({ parent_version_id: catalog?.current?.id, source, recommendation_text: source === "chatgpt_recommendation" ? recommendation : null }) });
      setRecommendation(""); await load();
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "Черновик не создан"); }
    finally { setBusy(false); }
  }

  async function openVersion(recipe: RecipeVersion) {
    try { setSelected(await api<RecipeVersion & { diff: RecipeDiff[] }>(`/api/recipes/${recipe.id}`)); }
    catch (reason) { setError(reason instanceof ApiError ? reason.message : "Версия не открылась"); }
  }

  if (!catalog && !error) return <Loading label="Открываю рецептуры…" />;
  if (!catalog) return <EmptyState title={error || "Рецептуры не загрузились"} action={() => void load()} />;
  const current = catalog.current;
  return <div className="page-stack recipes-page">
    <section className="page-heading"><div><p className="eyebrow">Питание газона</p><h2>Рецептуры</h2><p>Текущая программа, документы и полная история изменений.</p></div>{!catalog.draft && <button className="primary-button" onClick={() => void createDraft("manual")} disabled={busy}><Plus />Новая версия</button>}</section>
    {error && <div className="notice error" role="alert">{error}</div>}
    {current ? <section className="panel current-recipe"><div className="current-recipe-head"><div><span className="status-pill">Применяется сейчас</span><h3>{current.label}</h3><p>{formatDate(current.effective_from)} — {current.effective_to ? formatDate(current.effective_to) : "без окончания"} · зоны {current.zones.map((zone) => zone.zone_number).join(", ")}</p></div><div className="recipe-document-actions"><a className="secondary-button" href={`/api/recipes/${current.id}/document`} target="_blank" rel="noreferrer"><FileText />Документ установки</a><small>SHA‑256 {current.document_sha256?.slice(0, 12)}…</small></div></div><RecipeMonthView recipe={current} month={month} onMonth={setMonth} /><div className="recipe-provenance"><span><b>Основание</b>{current.change_reason}</span><span><b>Опубликовал</b>{current.published_by_name ?? "Импорт Google Sheets"}</span><span><b>Дата</b>{current.published_at ? formatDateTime(current.published_at) : "—"}</span></div></section> : <div className="notice error">На текущую дату нет опубликованной рецептуры. Расчёт внесения будет заблокирован.</div>}
    {catalog.draft ? <RecipeEditor key={catalog.draft.id} draft={catalog.draft} catalog={catalog} onReload={load} /> : <section className="panel recipe-ai-draft"><div className="panel-title"><div><p className="eyebrow">Контролируемое изменение</p><h3>Черновик из рекомендации ChatGPT</h3></div><GitCompare /></div><p>Вставьте рекомендацию для фиксации источника. Система скопирует текущую программу; дозировки нужно проверить и изменить вручную, публикация выполняется отдельно.</p><textarea rows={4} value={recommendation} onChange={(event) => setRecommendation(event.target.value)} placeholder="Рекомендация, выводы анализа и допущения" /><button className="secondary-button" onClick={() => void createDraft("chatgpt_recommendation")} disabled={busy || recommendation.trim().length < 10}>{busy ? <LoaderCircle className="spin" /> : <GitCompare />}Создать проверяемый черновик</button></section>}
    <section className="panel recipe-history"><div className="panel-title"><div><p className="eyebrow">Неизменяемый журнал</p><h3>История версий</h3></div><Clock3 /></div><div>{catalog.history.map((recipe) => <button key={recipe.id} onClick={() => void openVersion(recipe)}><span className={`status-pill ${recipe.display_status === "rejected" ? "waiting" : ""}`}>{recipeStatusLabels[recipe.display_status]}</span><strong>{recipe.label}</strong><small>{formatDate(recipe.effective_from)} — {recipe.effective_to ? formatDate(recipe.effective_to) : "∞"} · {recipe.change_reason ?? "Без описания"}</small><ChevronRight /></button>)}</div></section>
    {selected && <div className="photo-editor-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target) setSelected(null); }}><section className="photo-editor recipe-detail" role="dialog" aria-modal="true" aria-label="Версия рецептуры"><header><div><p className="eyebrow">{recipeStatusLabels[selected.display_status]}</p><h2>{selected.label}</h2></div><button className="icon-button" onClick={() => setSelected(null)} aria-label="Закрыть версию"><X /></button></header><div className="photo-editor-content"><p>{selected.change_reason ?? "Причина не указана"}</p><RecipeMonthView recipe={selected} month={month} onMonth={setMonth} />{selected.diff && selected.diff.length > 0 && <div className="recipe-diff"><strong>Изменения относительно {selected.parent_label}</strong>{selected.diff.map((item) => <span key={`${item.month}-${item.nomenclature_id}`}><b>{item.month_name} · {item.name}</b><em>{item.before ?? "—"} → {item.after ?? "—"} г</em></span>)}</div>}{selected.has_document && <a className="secondary-button" href={`/api/recipes/${selected.id}/document`} target="_blank" rel="noreferrer"><FileText />Открыть документ установки</a>}</div></section></div>}
  </div>;
}

type HydrawiseSettings = {
  api_key: string;
  storage: "database" | "environment";
  updated_at: string | null;
  sync_schedule: string;
  web_email: string | null;
  has_web_password: boolean;
  web_storage: "encrypted" | "not_configured";
};

type WaterStatus = {
  state: string; message: string; latest_at: string | null; mapped_zones?: number;
  weather_observations_available?: boolean; actual_runs_available?: boolean;
  actual_run_count?: number; weather_metric_count?: number;
  web_sync?: { status: string; started_at: string; finished_at: string | null; error_message: string | null } | null;
};
type WaterRun = {
  id: string; started_at: string; ended_at: string; duration_seconds: number; event_kind: string;
  reason: string | null; quality: string; zone_number: number | null; zone_name: string; photo_path: string | null;
};
type WeatherMetric = { date: string; metric_type: string; value: string; unit: string; quality: string };

function NutritionPage({ user }: { user: User }) {
  const monthNames = ["", "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
  const [data, setData] = useState<NutritionData | null>(null);
  const [season, setSeason] = useState<NutritionSeason | null>(null);
  const [adaptivePolicy, setAdaptivePolicy] = useState<AdaptivePolicyStatus | null>(null);
  const [fertikaDose, setFertikaDose] = useState<40 | 50>(50);
  const [fertikaConfirm, setFertikaConfirm] = useState(false);
  const [fertikaBusy, setFertikaBusy] = useState(false);
  const [fertikaKey, setFertikaKey] = useState(crypto.randomUUID());
  const [jarStarted, setJarStarted] = useState("");
  const [jarFinished, setJarFinished] = useState("");
  const [jarClear, setJarClear] = useState(false);
  const [jarChecked, setJarChecked] = useState(false);
  const [jarNotes, setJarNotes] = useState("");
  const [jarBusy, setJarBusy] = useState(false);
  const [month, setMonth] = useState(Math.min(10, Math.max(4, Number(moscowToday().slice(5, 7)))));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (manual = false) => {
    setLoading(true); setError("");
    try {
      const [payload, seasonPayload, policyPayload] = await Promise.all([
        api<NutritionData>(manual ? "/api/nutrition/refresh" : "/api/nutrition", manual ? { method: "POST" } : undefined),
        api<NutritionSeason>("/api/nutrition/season"),
        api<AdaptivePolicyStatus>("/api/nutrition/adaptive-policy")
      ]);
      setData(payload);
      setSeason(seasonPayload);
      setAdaptivePolicy(policyPayload);
      if (!payload.months.some((item) => item.month === month)) setMonth(payload.months[0]?.month ?? 7);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Баланс питания не рассчитан");
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const selected = data?.months.find((item) => item.month === month) ?? null;
  const qualityLabels: Record<string, string> = {
    measured_and_estimated: "Измеренные остатки + текущая оценка",
    estimated_timing: "Объём измерен, даты распределены оценочно",
    limited_data: "Недостаточно данных для текущего периода",
    reference_only_no_debt: "Справочно · без долга",
  };
  const formatNutrient = (value: string, unit: "g_m2" | "mg_m2") => `${formatNumber(value, unit === "g_m2" ? 2 : 1)} ${unit === "g_m2" ? "г/м²" : "мг/м²"}`;

  async function seasonAction(action: "start" | "finish" | "close", emergency = false) {
    setLoading(true); setError("");
    try {
      const payload = await api<NutritionSeason>(`/api/nutrition/season/${action}`, {
        method: "POST",
        body: action === "close" ? JSON.stringify({ emergency }) : undefined
      });
      setSeason(payload);
      if (action !== "start") await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Не удалось изменить состояние сезона");
    } finally { setLoading(false); }
  }

  async function confirmFertika() {
    setFertikaBusy(true); setError("");
    try {
      await api("/api/nutrition/external-credits/fertika", {
        method: "POST",
        headers: { "Idempotency-Key": fertikaKey },
        body: JSON.stringify({ applied_on: moscowToday(), dose_grams_m2: fertikaDose })
      });
      setFertikaConfirm(false); setFertikaKey(crypto.randomUUID());
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Внесение Fertika не зафиксировано");
    } finally { setFertikaBusy(false); }
  }

  async function recordJarTest() {
    setJarBusy(true); setError("");
    try {
      await api("/api/nutrition/qualifications/growth-3", {
        method: "POST",
        body: JSON.stringify({ started_at: jarStarted, finished_at: jarFinished, solution_clear: jarClear, injector_filter_checked: jarChecked, notes: jarNotes })
      });
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Протокол пробы не сохранён");
    } finally { setJarBusy(false); }
  }

  if (loading && !data) return <Loading label="Сверяю заправки, поливы и составы…" />;
  return <div className="page-stack">
    <section className="page-heading nutrition-heading">
      <div><p className="eyebrow">Аналитика без изменения рецептуры</p><h2>Баланс питания</h2><p>Цель, возможности текущей рецептуры и расчётно внесённые элементы — отдельно.</p></div>
      <button className="secondary-button" onClick={() => void load(true)} disabled={loading}>{loading ? <LoaderCircle className="spin" /> : <RefreshCw />}Обновить</button>
    </section>
    {error && <div className="notice error" role="alert">{error}</div>}
    {data && <>
      {adaptivePolicy && <section className="panel adaptive-policy-card">
        <div className="panel-title"><div><p className="eyebrow">Следующий сезон · расчёт уже установлен</p><h3>Адаптивная фертигация {adaptivePolicy.version}</h3></div><span className="status-pill">С {formatDate(adaptivePolicy.effective_from)}</span></div>
        <p>Система считает скользящую 14-дневную цель, фактическую доставку по остатку и Hydrawise и меняет уровень только после полного окна наблюдения. Летний долг не переносится в осень или новую весну.</p>
        <div className="adaptive-levels">
          {[["growth_1", "Growth 1"], ["growth_2", "Growth 2"], ["growth_3", "Growth 3"]].map(([code, label]) => <span key={code}><small>{label}</small><strong>{formatNumber(Number(adaptivePolicy.levels[code]?.N ?? 0) / 1000, 1)} г N/м²</strong><em>за 14 дней</em></span>)}
        </div>
        {adaptivePolicy.calculation_deadline.state !== "not_scheduled" && <div className={`notice ${adaptivePolicy.calculation_deadline.state === "overdue" || adaptivePolicy.calculation_deadline.state === "required" ? "error" : adaptivePolicy.calculation_deadline.state === "preliminary" ? "warning" : "neutral"}`}><Clock3 /><div><strong>Подготовка следующей заправки</strong><span>{adaptivePolicy.calculation_deadline.message}{adaptivePolicy.calculation_deadline.next_refill_latest ? ` Прогнозная граница — ${formatDate(adaptivePolicy.calculation_deadline.next_refill_latest)}.` : ""}</span></div></div>}
        <div className="notice warning"><div><strong>До старта сезона</strong><span>Заменить сульфат магния марки А на водорастворимый продукт для фертигации. Growth 3 останется заблокирован до сохранённой 48-часовой холодной пробы.</span></div></div>
        {adaptivePolicy.growth_3_qualification?.result === "passed" ? <div className="notice success"><div><strong>Growth 3 технически разрешён</strong><span>48-часовая проба и инжектор/фильтр проверены. Протокол {adaptivePolicy.growth_3_qualification.snapshot_sha256.slice(0, 12)}…</span></div></div> : <details className="jar-test"><summary>Сохранить протокол 48-часовой холодной пробы</summary><div><label><span>Начало пробы</span><input type="datetime-local" value={jarStarted} onChange={(event) => setJarStarted(event.target.value)} /></label><label><span>Окончание пробы</span><input type="datetime-local" value={jarFinished} onChange={(event) => setJarFinished(event.target.value)} /></label><label className="check-row"><input type="checkbox" checked={jarClear} onChange={(event) => setJarClear(event.target.checked)} /><span>Раствор остался прозрачным, без осадка и кристаллов</span></label><label className="check-row"><input type="checkbox" checked={jarChecked} onChange={(event) => setJarChecked(event.target.checked)} /><span>Инжектор и фильтр проверены</span></label><label><span>Комментарий <small>необязательно</small></span><textarea rows={2} value={jarNotes} onChange={(event) => setJarNotes(event.target.value)} /></label><button className="secondary-button" onClick={() => void recordJarTest()} disabled={jarBusy || !jarStarted || !jarFinished}>{jarBusy ? <LoaderCircle className="spin" /> : <ShieldCheck />}Сохранить результат</button></div></details>}
        {adaptivePolicy.fertika_credit ? <div className="notice success"><div><strong>Весенняя Fertika учтена</strong><span>{formatNumber(adaptivePolicy.fertika_credit.dose_grams_m2)} г/м² внесено {formatDate(adaptivePolicy.fertika_credit.applied_on)}; ежедневный кредит действует до {formatDate(adaptivePolicy.fertika_credit.credit_to)}.</span></div></div> : season?.year && season.year >= 2027 && season.state === "active_growth" && <div className="fertika-confirmation">
          <div><strong>Однократная весенняя Fertika</strong><span>Выберите только фактически внесённую норму. Элементы и списание со склада система рассчитает сама.</span></div>
          <select value={fertikaDose} onChange={(event) => { setFertikaDose(Number(event.target.value) as 40 | 50); setFertikaConfirm(false); }}><option value={40}>40 г/м² · 24 кг</option><option value={50}>50 г/м² · 30 кг</option></select>
          {!fertikaConfirm ? <button className="secondary-button" onClick={() => setFertikaConfirm(true)}>Fertika внесена</button> : <div className="control-confirm"><strong>Подтвердите фактическое внесение</strong><span>Сегодня будет списано {fertikaDose === 50 ? "30" : "24"} кг и создано 14 неизменяемых дневных долей.</span><button className="primary-button" onClick={() => void confirmFertika()} disabled={fertikaBusy}>{fertikaBusy ? <><LoaderCircle className="spin" />Сохраняю…</> : "Подтвердить внесение"}</button></div>}
        </div>}
        <small>Версия зафиксирована: SHA‑256 {adaptivePolicy.snapshot_sha256.slice(0, 12)}…</small>
      </section>}
      {season && <section className={`panel season-lifecycle ${season.state}`}>
        <div className="season-lifecycle-main">
          <div className="season-state-icon"><Droplets /></div>
          <div><p className="eyebrow">Сезон {season.year}</p><h3>{season.label}</h3><p>{season.next_action}</p></div>
        </div>
        <div className="season-facts">
          <span><small>Фактический старт</small><strong>{season.active_from ? formatDate(season.active_from) : "Ещё не подтверждён"}</strong></span>
          <span><small>Переход к осени</small><strong>{formatDate(season.policy.growth_debt_resets_on)}</strong><em>следует текущей рецептуре</em></span>
          <span><small>Перенос долга</small><strong>Только внутри фазы</strong><em>осенью и весной начинается новый баланс</em></span>
        </div>
        {can(user, "nutrition.season.manage") && <div className="season-actions">
          {season.can_start && <button className="primary-button" onClick={() => void seasonAction("start")} disabled={loading}>Начать сезон</button>}
          {season.can_finish && <button className="secondary-button" onClick={() => void seasonAction("finish")} disabled={loading}>Начать завершение сезона</button>}
          {season.can_close && <><button className="primary-button" onClick={() => void seasonAction("close")} disabled={loading}>Hydrawise остановлен · закрыть сезон</button><button className="text-button danger" onClick={() => void seasonAction("close", true)} disabled={loading}>Аварийно закрыть сейчас</button></>}
        </div>}
        <details className="season-evidence"><summary>Почему осенняя граница сейчас здесь</summary><p>В действующей рецептуре карбамид меняется с {formatNumber(season.policy.boundary_evidence.august_grams_per_100_liters ?? 0)} г/100 л в августе до {formatNumber(season.policy.boundary_evidence.september_grams_per_100_liters ?? 0)} г/100 л в сентябре. При публикации новой рецептуры граница пересчитывается как часть её сезонной политики.</p></details>
      </section>}
      <section className="nutrition-summary">
        <article className="nutrition-program"><div><Leaf /><span>Программа {data.program.version}</span></div><strong>{data.program.name}</strong><p>{data.program.source_note}</p><small>Источник: {data.program.source} · площадь {formatNumber(data.program.area_m2)} м² · расчёт с {formatDate(data.program.actual_tracking_from)}</small></article>
        <article><span>Качество расчёта</span><strong>{data.quality_label}</strong><p>{data.rate.closed_intervals} закрытых интервала · {data.rate.calendar_liters_per_day ? `${formatNumber(data.rate.calendar_liters_per_day, 1)} л/день` : "темп ещё не определён"}</p><small>Обновлено {formatDateTime(data.calculated_at)}</small></article>
        <article><span>Последняя заправка</span><strong>{data.tank.latest_fill_date ? formatDate(data.tank.latest_fill_date) : "Нет данных"}</strong><p>Бак {formatNumber(data.tank.capacity_liters)} л</p><small>Текущий период: {data.tank.open_interval_quality === "estimated_from_hydrawise" ? "оценка по Hydrawise" : data.tank.open_interval_quality === "estimated_capped_at_tank" ? "оценка ограничена объёмом бака" : "ожидает калибровки"}</small></article>
      </section>
      <div className="section-tabs nutrition-months" aria-label="Месяц баланса">{data.months.map((item) => <button key={item.month} className={month === item.month ? "active" : ""} onClick={() => setMonth(item.month)}>{monthNames[item.month]}{item.reference_only && <small>справочно</small>}</button>)}</div>
      {selected && <>
        {selected.reference_only && <div className="notice neutral"><strong>{monthNames[selected.month]} 2026 — только ориентир.</strong> Эксперимент начался 3 июня: недовнесение за апрель и май не переносится и не компенсируется.</div>}
        <section className="nutrition-grid">
          {selected.nutrients.map((item) => {
            const completion = Number(item.completion_percent);
            const signal = selected.reference_only ? "neutral" : completion < 60 ? "red" : completion < 85 ? "yellow" : completion > 125 ? "yellow" : "green";
            return <details className={`nutrition-card ${signal}`} key={item.code}>
              <summary>
                <div className="nutrition-card-title"><span>{item.code}</span><div><strong>{item.name}</strong><small>{qualityLabels[item.quality] ?? item.quality}</small></div></div>
                <div className="nutrition-score"><strong>{selected.reference_only ? "—" : `${formatNumber(item.completion_percent, 0)}%`}</strong><small>факт от цели</small></div>
                <div className="nutrition-progress"><i style={{ width: `${selected.reference_only ? 0 : Math.min(100, Math.max(0, completion))}%` }} /></div>
              </summary>
              <div className="nutrition-values">
                <span><small>Цель</small><strong>{formatNutrient(item.target, item.display_unit)}</strong></span>
                <span><small>Ожидание по рецептуре</small><strong>{formatNutrient(item.expected, item.display_unit)}</strong><em>{item.recipe_fit_percent ? `${formatNumber(item.recipe_fit_percent, 0)}% цели` : "цель равна нулю"}</em></span>
                <span><small>Расчётно внесено</small><strong>{formatNutrient(item.delivered, item.display_unit)}</strong></span>
                <span><small>Прогноз конца месяца</small><strong>{formatNutrient(item.projected, item.display_unit)}</strong></span>
              </div>
            </details>;
          })}
        </section>
      </>}
      <section className="panel nutrition-details">
        <div className="panel-title"><div><p className="eyebrow">Проверяемый расчёт</p><h3>Откуда получились цифры</h3></div><FileText /></div>
        <div className="nutrition-intervals">{data.intervals.map((item, index) => <article key={`${item.from}-${item.to}-${index}`}><div><strong>{formatDate(item.from)}–{formatDate(item.to)}</strong><small>{item.quality === "measured_total_runtime_allocated" ? "объём по остатку, дни по Hydrawise" : item.quality.startsWith("estimated") ? "текущий оценочный период" : "объём измерен, распределение по дням оценочное"}</small></div><b>{formatNumber(item.consumed_liters, 1)} л</b><span>{formatNumber(item.runtime_minutes, 0)} мин</span></article>)}</div>
        <details className="assumption-list composition-list"><summary>Проверенные составы материалов</summary><div>{data.compositions.map((item) => <article key={`${item.material}-${item.version}`}><div><strong>{item.material}</strong><small>{item.product_label}{item.manufacturer ? ` · ${item.manufacturer}` : ""}</small></div><span>{Object.entries(item.contents_percent).map(([code, percent]) => `${code} ${formatNumber(percent, 3)}%`).join(" · ")}</span><small>{item.source}</small></article>)}</div></details>
        <details className="assumption-list"><summary>Допущения и границы точности</summary><ul>{data.assumptions.map((item) => <li key={item}>{item}</li>)}</ul></details>
      </section>
    </>}
  </div>;
}

function WaterPage({ user }: { user: User }) {
  const [tab, setTab] = useState<"overview" | "history" | "weather" | "plans">("overview");
  const [status, setStatus] = useState<WaterStatus | null>(null);
  const [schedules, setSchedules] = useState<Array<{ zone_number: number; name: string; mapping_state: string; planned_run_minutes: number | null; next_start_text: string | null; quality: string | null }>>([]);
  const [runs, setRuns] = useState<WaterRun[]>([]);
  const [weather, setWeather] = useState<WeatherMetric[]>([]);
  const [settings, setSettings] = useState<HydrawiseSettings | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [webEmail, setWebEmail] = useState("");
  const [webPassword, setWebPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [savingKey, setSavingKey] = useState(false);
  const [keyError, setKeyError] = useState("");
  const [keySaved, setKeySaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const requests: Promise<unknown>[] = [
      api<WaterStatus>("/api/hydrawise/status"),
      api<typeof schedules>("/api/hydrawise/zones"),
      api<WaterRun[]>("/api/hydrawise/history?days=30"),
      api<WeatherMetric[]>("/api/hydrawise/weather?days=30")
    ];
    if (can(user, "integration.settings")) requests.push(api<HydrawiseSettings>("/api/hydrawise/settings"));
    try {
      const results = await Promise.all(requests);
      setStatus(results[0] as typeof status);
      setSchedules(results[1] as typeof schedules);
      setRuns(results[2] as WaterRun[]);
      setWeather(results[3] as WeatherMetric[]);
      if (can(user, "integration.settings") && results[4]) {
        const loadedSettings = results[4] as HydrawiseSettings;
        setSettings(loadedSettings);
        setApiKey(loadedSettings.api_key);
        setWebEmail(loadedSettings.web_email ?? "");
      }
    } finally { setLoading(false); }
  }, [user.role]);
  useEffect(() => { void load(); }, [load]);

  async function saveApiKey(event: FormEvent) {
    event.preventDefault();
    setSavingKey(true); setKeyError(""); setKeySaved(false);
    try {
      const saved = await api<HydrawiseSettings>("/api/hydrawise/settings", {
        method: "PUT",
        body: JSON.stringify({ api_key: apiKey, web_email: webEmail || null, ...(webPassword ? { web_password: webPassword } : {}) })
      });
      setSettings(saved); setApiKey(saved.api_key); setWebPassword(""); setKeySaved(true);
    } catch (reason) {
      setKeyError(reason instanceof ApiError ? reason.message : "Ключ не сохранён");
    } finally { setSavingKey(false); }
  }

  async function syncNow() {
    setSyncing(true); setKeyError("");
    try { await api("/api/hydrawise/sync", { method: "POST" }); await load(); }
    catch (reason) { setKeyError(reason instanceof ApiError ? reason.message : "Синхронизация не завершилась"); }
    finally { setSyncing(false); }
  }

  const totalMinutes = runs.reduce((sum, item) => sum + item.duration_seconds / 60, 0);
  const rain = weather.filter((item) => item.metric_type === "station_rainfall").reduce((sum, item) => sum + Number(item.value), 0);
  const et = weather.filter((item) => item.metric_type === "evapotranspiration").reduce((sum, item) => sum + Number(item.value), 0);
  const metricLabels: Record<string, string> = {
    station_temperature: "Температура", station_rainfall: "Осадки", evapotranspiration: "Испарение ET",
    water_saving: "Экономия воды", virtual_solar_sync: "Virtual Solar Sync", sensor_level: "Датчик",
    weather_forecast: "Прогноз", smart_water_balance: "Водный баланс",
    monthly_evapotranspiration: "Климатическая ET", monthly_temperature_rainfall: "Климатическая норма"
  };
  const kindLabels: Record<string, string> = { manual: "Ручной", automatic: "Автоматический", weather_adjusted: "С учётом погоды" };

  return <div className="page-stack"><section className="page-heading"><div><p className="eyebrow">Полив и погода</p><h2>Hydrawise</h2></div>{can(user, "integration.sync") && <button className="secondary-button" onClick={syncNow} disabled={syncing}>{syncing ? <><LoaderCircle className="spin" />Загружаю…</> : <><RefreshCw />Синхронизировать</>}</button>}</section>
    <div className="section-tabs"><button className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")}>Обзор</button><button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>История</button><button className={tab === "weather" ? "active" : ""} onClick={() => setTab("weather")}>Погода</button><button className={tab === "plans" ? "active" : ""} onClick={() => setTab("plans")}>Планы</button></div>
    {keyError && <div className="notice error" role="alert">{keyError}</div>}
    {loading ? <Loading label="Собираю полив и погоду…" /> : tab === "overview" ? <><section className="water-kpis"><article><span>Фактические запуски · 30 дней</span><strong>{runs.length}</strong></article><article><span>Время полива</span><strong>{formatNumber(totalMinutes, 0)} мин</strong></article><article><span>Осадки</span><strong>{formatNumber(rain, 1)} мм</strong></article><article><span>Испарение ET</span><strong>{formatNumber(et, 1)} мм</strong></article></section><section className="panel integration-summary"><div className="weather-art"><CloudSun /></div><div><span className={`status-pill ${status?.web_sync?.status === "failed" ? "waiting" : ""}`}>{status?.web_sync?.status === "failed" ? "Частично" : "Данные и управление"}</span><h3>{status?.actual_runs_available ? "Фактическая история сохраняется" : status?.message ?? "Ожидаю данные"}</h3><p>Связано зон: {status?.mapped_zones ?? 0} из 7. Ручное управление доступно из карточек зон; история и погода сохраняются отдельно.</p><small>{status?.latest_at ? `Официальный API: ${formatDateTime(status.latest_at)}` : "Официальный API ещё не синхронизирован"}</small></div></section>
      {can(user, "integration.settings") && <form className="panel hydrawise-settings" onSubmit={saveApiKey}><div className="panel-title"><div><p className="eyebrow">Настройка владельца</p><h3>Доступ к Hydrawise</h3></div><ShieldCheck /></div><div className="hydrawise-credential-grid"><label><span>API key</span><input type="text" autoComplete="off" spellCheck={false} value={apiKey} minLength={8} maxLength={255} onChange={(event) => { setApiKey(event.target.value); setKeySaved(false); }} required /></label><label><span>Email веб‑кабинета</span><input type="email" autoComplete="username" value={webEmail} onChange={(event) => setWebEmail(event.target.value)} /></label><label><span>Пароль веб‑кабинета</span><input type="password" autoComplete="new-password" minLength={8} value={webPassword} onChange={(event) => setWebPassword(event.target.value)} placeholder={settings?.has_web_password ? "Сохранён зашифрованно" : "Нужен для истории и погоды"} /></label></div><p>API key по вашему решению хранится открыто. Пароль веб‑кабинета хранится отдельно и зашифрован; обратно в интерфейс он не выводится.</p><div className="sync-schedule"><RefreshCw /><span><strong>Автосинхронизация</strong>{settings?.sync_schedule ?? "Ежедневно в 11:00 по Москве"}</span></div>{keySaved && <div className="notice success" role="status">Настройки проверены и сохранены.</div>}<button className="primary-button" disabled={savingKey}>{savingKey ? <><LoaderCircle className="spin" />Сохраняю…</> : "Проверить и сохранить"}</button></form>}</>
      : tab === "history" ? <section className="water-history">{runs.length ? runs.map((run) => <article key={run.id}><div className="water-history-photo">{run.photo_path ? <img src={run.photo_path} alt="" /> : <Droplets />}</div><div><div className="water-history-heading"><span>Зона {run.zone_number ?? "—"}</span><span className="status-pill">Факт</span></div><h3>{run.zone_name}</h3><p>{run.reason || kindLabels[run.event_kind] || "Полив"}</p><small>{formatDateTime(run.started_at)} · {formatNumber(run.duration_seconds / 60, 1)} мин · {kindLabels[run.event_kind] ?? run.event_kind}</small></div></article>) : <div className="notice neutral">Фактические запуски пока не загружены. Уже сохранённые данные здесь не удаляются при сбое Hydrawise.</div>}</section>
      : tab === "weather" ? <section className="weather-table">{weather.length ? weather.map((metric, index) => <article key={`${metric.date}-${metric.metric_type}-${index}`}><time>{formatDate(metric.date)}</time><div><strong>{metricLabels[metric.metric_type] ?? metric.metric_type}</strong><small>{metric.quality === "actual" ? "Наблюдение" : metric.quality === "forecast" ? "Прогноз" : metric.quality === "climate_reference" ? "Климатическая норма" : "Расчёт Hydrawise"}</small></div><b>{formatNumber(metric.value, 1)} {metric.unit}</b></article>) : <div className="notice neutral">Погодные показатели пока не загружены.</div>}</section>
      : <section className="irrigation-grid">{schedules.map((zone) => <article className="irrigation-card" key={zone.zone_number}><div><span>Зона {zone.zone_number}</span><span className="status-pill">План</span></div><h3>{zone.name}</h3><strong>{zone.planned_run_minutes === null ? "—" : `${formatNumber(zone.planned_run_minutes, 1)} мин`}</strong><small>{zone.next_start_text ? `Время из Hydrawise: ${zone.next_start_text}` : "Следующий запуск не указан"}</small></article>)}</section>}
  </div>;
}

const shareScopeOptions = [
  { id: "journal", label: "Журнал внесений" },
  { id: "recipes", label: "Рецептуры и история" },
  { id: "stock", label: "Склад и документы" },
  { id: "forecast", label: "Прогноз остатков и закупки" },
  { id: "nutrition", label: "Баланс питания и целевые нормы" },
  { id: "zones", label: "Зоны участка" },
  { id: "irrigation_weather", label: "Полив и погода" },
  { id: "comments", label: "Комментарии" }
];

function AnalysisPage({ user, zones }: { user: User; zones: Zone[] }) {
  const [from, setFrom] = useState("2026-06-01");
  const [to, setTo] = useState(moscowToday());
  const [groupBy, setGroupBy] = useState<"day" | "week" | "month">("week");
  const [zoneNumber, setZoneNumber] = useState("");
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [analyticsError, setAnalyticsError] = useState("");
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [scopes, setScopes] = useState(shareScopeOptions.map((scope) => scope.id));
  const [shareId, setShareId] = useState("");
  const [url, setUrl] = useState("");
  const [csvUrl, setCsvUrl] = useState("");
  const [revoked, setRevoked] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true); setAnalyticsError("");
    const params = new URLSearchParams({ date_from: from, date_to: to, group_by: groupBy });
    if (zoneNumber) params.set("zone_number", zoneNumber);
    try {
      setAnalytics(await api<AnalyticsData>(`/api/analytics/water?${params}`));
    } catch (reason) {
      setAnalyticsError(reason instanceof ApiError ? reason.message : "Не удалось собрать аналитику");
    } finally { setAnalyticsLoading(false); }
  }, [from, to, groupBy, zoneNumber]);

  useEffect(() => { void loadAnalytics(); }, [loadAnalytics]);

  async function create(event: FormEvent) {
    event.preventDefault(); setLoading(true); setError(""); setRevoked(false);
    try {
      const result = await api<{ id: string; url: string; data_url: string; csv_url: string }>("/api/shares", { method: "POST", body: JSON.stringify({ date_from: from, date_to: to, scopes }) });
      setShareId(result.id);
      setUrl(result.data_url);
      setCsvUrl(result.csv_url);
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "Не удалось создать ссылку"); }
    finally { setLoading(false); }
  }
  async function revoke() {
    if (!shareId) return;
    setLoading(true); setError("");
    try {
      await api(`/api/shares/${shareId}/revoke`, { method: "POST" });
      setShareId(""); setUrl(""); setCsvUrl(""); setRevoked(true);
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "Не удалось отозвать ссылку"); }
    finally { setLoading(false); }
  }
  const maxIrrigation = Math.max(...(analytics?.series.map((row) => Number(row.irrigation_minutes)) ?? []), 1);
  const maxWeather = Math.max(...(analytics?.series.flatMap((row) => [Number(row.rainfall_mm), Number(row.evapotranspiration_mm)]) ?? []), 1);
  return <div className="page-stack"><section className="page-heading"><div><p className="eyebrow">Факты по участку</p><h2>Анализ</h2></div></section>
    <form className="panel analytics-controls" onSubmit={(event) => { event.preventDefault(); void loadAnalytics(); }}><div className="date-pair"><label><span>С</span><input type="date" value={from} onChange={(event) => setFrom(event.target.value)} required /></label><label><span>По</span><input type="date" value={to} onChange={(event) => setTo(event.target.value)} required /></label></div><label><span>Группировка</span><select value={groupBy} onChange={(event) => setGroupBy(event.target.value as "day" | "week" | "month")}><option value="day">По дням</option><option value="week">По неделям</option><option value="month">По месяцам</option></select></label><label><span>Зона полива</span><select value={zoneNumber} onChange={(event) => setZoneNumber(event.target.value)}><option value="">Все зоны</option>{zones.map((zone) => <option key={zone.id} value={zone.zone_number}>Зона {zone.zone_number} · {zone.name}</option>)}</select></label><button className="secondary-button" disabled={analyticsLoading}>{analyticsLoading ? <><LoaderCircle className="spin" />Собираю…</> : "Обновить анализ"}</button></form>
    {analyticsError && <div className="notice error">{analyticsError}</div>}
    {analytics && <><section className="water-kpis"><article><span>Фактический полив</span><strong>{formatNumber(analytics.summary.irrigation_minutes, 1)} мин</strong></article><article><span>Осадки станции</span><strong>{formatNumber(analytics.summary.rainfall_mm, 1)} мм</strong></article><article><span>ET Hydrawise</span><strong>{formatNumber(analytics.summary.evapotranspiration_mm, 1)} мм</strong></article><article><span>Внесения</span><strong>{analytics.summary.application_count}</strong><span>{formatNumber(analytics.summary.refill_liters, 1)} л долива</span></article></section><section className="panel analytics-chart"><div className="panel-title"><div><p className="eyebrow">Динамика</p><h3>Полив, осадки и ET</h3></div><Droplets /></div><div className="analytics-legend"><span className="irrigation">Полив, мин</span><span className="rain">Осадки, мм</span><span className="et">ET, мм</span></div><div className="analytics-series">{analytics.series.map((row) => <article key={row.period_start}><time>{formatDate(row.period_start)}</time><div><span className="irrigation" style={{ width: `${Number(row.irrigation_minutes) / maxIrrigation * 100}%` }} /><b>{formatNumber(row.irrigation_minutes, 1)} мин</b></div><div><span className="rain" style={{ width: `${Number(row.rainfall_mm) / maxWeather * 100}%` }} /><b>{formatNumber(row.rainfall_mm, 1)} мм</b></div><div><span className="et" style={{ width: `${Number(row.evapotranspiration_mm) / maxWeather * 100}%` }} /><b>{formatNumber(row.evapotranspiration_mm, 1)} мм</b></div></article>)}</div><div className="notice neutral">{analytics.limitations.join(" ")}</div></section></>}
    {can(user, "share.manage") && <form className="panel form-panel" onSubmit={create}><div className="panel-title"><div><p className="eyebrow">Безопасная передача</p><h3>Ссылка для ChatGPT и CSV</h3></div><Link2 /></div><div className="scope-list"><span>Выберите состав данных</span><div className="scope-options">{shareScopeOptions.map((scope) => <label key={scope.id}><input type="checkbox" checked={scopes.includes(scope.id)} onChange={() => setScopes((current) => current.includes(scope.id) ? current.filter((item) => item !== scope.id) : [...current, scope.id])} /><span>{scope.label}</span></label>)}</div></div><div className="notice neutral">Ссылка работает 7 дней, доступна только для чтения и отзывается пользователем с рабочим доступом. Для гарантированного чтения подключите приложение Gazon в ChatGPT один раз. <a href="/about" target="_blank" rel="noreferrer">Инструкция подключения</a>. CSV скачивается архивом: каждый раздел в отдельном файле.</div>{revoked && <div className="notice neutral" role="status">Ссылка отозвана и больше недоступна.</div>}{error && <div className="notice error">{error}</div>}<button className="primary-button" disabled={loading || scopes.length === 0}>{loading ? <><LoaderCircle className="spin" />Создаю…</> : "Создать ссылку"}</button>{url && <div className="share-result"><span>Ссылка для анализа готова</span><a href={url} target="_blank" rel="noreferrer">{url}</a><button type="button" className="secondary-button" onClick={() => navigator.clipboard.writeText(url)}>Скопировать для ChatGPT</button><a className="secondary-button" href={csvUrl}><Download />Скачать CSV</a><button type="button" className="secondary-button" onClick={revoke} disabled={loading}>Отозвать ссылку</button></div>}</form>}
  </div>;
}

type AccountRow = { id: number; email: string; name: string; role: string; state: "active" | "invited" | "activation_expired" };

function AccessPage() {
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [name, setName] = useState("Садовник");
  const [email, setEmail] = useState("");
  const [activationUrl, setActivationUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loadAccounts = useCallback(async () => {
    try { setAccounts(await api<AccountRow[]>("/api/auth/accounts")); }
    catch (reason) { setError(reason instanceof ApiError ? reason.message : "Не удалось загрузить доступы"); }
  }, []);
  useEffect(() => { void loadAccounts(); }, [loadAccounts]);
  async function invite(event: FormEvent) {
    event.preventDefault(); setLoading(true); setError(""); setActivationUrl("");
    try {
      const result = await api<{ activation_url: string; expires_at: string }>("/api/auth/gardener-activations", { method: "POST", body: JSON.stringify({ name, email }) });
      setActivationUrl(result.activation_url); setExpiresAt(result.expires_at); setEmail("");
      await loadAccounts();
    } catch (reason) { setError(reason instanceof ApiError ? reason.message : "Не удалось создать приглашение"); }
    finally { setLoading(false); }
  }
  const stateLabel = { active: "Активен", invited: "Ожидает активации", activation_expired: "Ссылка истекла" };
  return <div className="page-stack compact"><section className="page-heading"><div><p className="eyebrow">Пользователи Gazon</p><h2>Доступ</h2></div></section><section className="panel"><div className="panel-title"><div><p className="eyebrow">Текущее состояние</p><h3>Учётные записи</h3></div><ShieldCheck /></div><div className="account-list">{accounts.map((account) => <article key={account.id}><div className="avatar">{account.name.slice(0, 1).toUpperCase()}</div><div><strong>{account.name}</strong><span>{account.email} · {account.role === "owner" ? "Владелец" : "Садовник"}</span></div><span className={`status-pill ${account.state === "active" ? "" : "waiting"}`}>{stateLabel[account.state]}</span></article>)}</div></section><form className="panel form-panel" onSubmit={invite}><div className="panel-title"><div><p className="eyebrow">Новый пользователь</p><h3>Пригласить садовника</h3></div><UserRound /></div><label><span>Имя</span><input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} required /></label><label><span>Email садовника</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></label><div className="notice neutral">Ссылка действует 24 часа. Садовник сам задаст пароль и получит рабочий доступ; настройки интеграций и изменение зон останутся доступны только владельцу.</div>{error && <div className="notice error">{error}</div>}<button className="primary-button" disabled={loading}>{loading ? <><LoaderCircle className="spin" />Создаю приглашение…</> : "Создать приглашение"}</button>{activationUrl && <div className="share-result"><span>Приглашение готово до {formatDateTime(expiresAt)}</span><a href={activationUrl} target="_blank" rel="noreferrer">{activationUrl}</a><button type="button" className="secondary-button" onClick={() => navigator.clipboard.writeText(activationUrl)}>Скопировать ссылку</button></div>}</form></div>;
}

function Loading({ label }: { label: string }) { return <div className="loading-state"><LoaderCircle className="spin" /><span>{label}</span></div>; }
function EmptyState({ title, action }: { title: string; action: () => void }) { return <div className="empty-state"><p>{title}</p><button className="secondary-button" onClick={action}>Повторить</button></div>; }

export default function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [page, setPage] = useState<Page>("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [journal, setJournal] = useState<Application[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [stock, setStock] = useState<StockItem[]>([]);
  const [stockDocuments, setStockDocuments] = useState<StockDocument[]>([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [dashboardData, journalData, zoneData, stockData, stockDocumentData] = await Promise.all([
        api<DashboardData>("/api/dashboard"), api<Application[]>("/api/applications?limit=50"), api<Zone[]>("/api/zones"),
        api<StockItem[]>("/api/stock"), api<StockDocument[]>("/api/stock/documents?limit=50")
      ]);
      setDashboard(dashboardData); setJournal(journalData); setZones(zoneData); setStock(stockData); setStockDocuments(stockDocumentData);
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 401) setUser(null);
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => {
    bootstrap().then(() => api<User>("/api/auth/me")).then(setUser).catch(() => setUser(null));
  }, []);
  useEffect(() => { void loadData(); }, [loadData]);

  async function logout() { try { await api("/api/auth/logout", { method: "POST" }); } finally { await bootstrap(); setUser(null); } }
  const displayPage = page;
  const currentTitle = useMemo(() => navigation.find((item) => item.id === displayPage)?.label ?? "Газон", [displayPage]);
  const activationToken = window.location.pathname.match(/^\/activate\/([^/]+)$/)?.[1] ?? "";
  if (user === undefined) return <Loading label="Открываю Газон…" />;
  if (!user && activationToken) return <Activation token={activationToken} onActivated={setUser} />;
  if (!user) return <Login onLogin={setUser} />;
  const visibleNavigation = navigation;

  const content = displayPage === "dashboard" ? <Dashboard data={dashboard} loading={loading} reload={loadData} />
    : displayPage === "new" ? <NewApplication onSaved={loadData} onOpenRecipes={() => setPage("recipes")} />
    : displayPage === "journal" ? <Journal rows={journal} loading={loading} user={user} onSaved={loadData} />
    : displayPage === "recipes" ? <RecipesPage />
    : displayPage === "nutrition" ? <NutritionPage user={user} />
    : displayPage === "stock" ? <StockPage items={stock} documents={stockDocuments} user={user} loading={loading} onSaved={loadData} />
    : displayPage === "zones" ? <ZonesPage zones={zones} loading={loading} user={user} onSaved={loadData} />
    : displayPage === "water" ? <WaterPage user={user} />
    : displayPage === "access" ? <AccessPage /> : <AnalysisPage user={user} zones={zones} />;

  return <div className="app-shell">
    <aside className={`sidebar ${menuOpen ? "open" : ""}`}>
      <div className="sidebar-brand"><div className="brand-mark"><Leaf /></div><div><strong>Газон</strong><span>управление участком</span></div><button className="close-menu" onClick={() => setMenuOpen(false)}><X /></button></div>
      <nav>{visibleNavigation.map((item) => { const Icon = item.icon; return <button key={item.id} className={displayPage === item.id ? "active" : ""} onClick={() => { setPage(item.id); setMenuOpen(false); }}><Icon /><span>{item.label}</span></button>; })}</nav>
      <div className="sidebar-user"><div className="avatar">{user.name.slice(0, 1).toUpperCase()}</div><div><strong>{user.name}</strong><span>{user.role === "owner" ? "Владелец" : "Садовник"}</span></div><button onClick={logout} aria-label="Выйти"><LogOut /></button></div>
    </aside>
    {menuOpen && <button className="scrim" onClick={() => setMenuOpen(false)} aria-label="Закрыть меню" />}
    <main className="content"><header className="mobile-header"><button onClick={() => setMenuOpen(true)}><Menu /></button><strong>{currentTitle}</strong><div className="avatar small">{user.name.slice(0, 1).toUpperCase()}</div></header>{content}</main>
    <nav className="bottom-nav">{navigation.filter((item) => ["dashboard", "new", "journal", "recipes", "nutrition", "zones", "water"].includes(item.id)).map((item) => { const Icon = item.icon; return <button key={item.id} className={page === item.id ? "active" : ""} onClick={() => setPage(item.id)}><Icon /><span>{item.id === "nutrition" ? "Питание" : item.label}</span></button>; })}</nav>
  </div>;
}
